
import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Button,
  TablePagination,
  Chip,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Switch,
} from "@mui/material";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import LogoutIcon from "@mui/icons-material/Logout";
import RefreshIcon from "@mui/icons-material/Refresh";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import SearchIcon from "@mui/icons-material/Search";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import SaveIcon from "@mui/icons-material/Save";
import { useAuth } from "../context/AuthContext";
import { useThemeMode } from "../context/ThemeContext";
import { useNavigate, Navigate } from "react-router-dom";
import axios from "axios";
import { ROLE_LABELS, ROLE_SELECT_MENU_PROPS, getAssignableRoles } from "../utils/roleOptions";
import AddUserDialog from "../components/AddUserDialog";
import { getApiErrorMessage } from "../utils/apiError";

function toUiRoleLabel(roles = []) {
  if (!roles.length) return "User";
  if (roles.includes("admin")) return "Admin";
  return roles.map((role) => ROLE_LABELS[role] || role).join(", ");
}

function formatTokens(value) {
  return Number(value || 0).toLocaleString();
}

function normalizeTokenLimit(value) {
  const parsed = parseFloat(String(value ?? "").replace(/,/g, ""));
  if (!Number.isFinite(parsed) || parsed < 0) return 0;
  return Math.floor(parsed * 1_000_000);
}

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString();
}

function formatFinalBrd(finalVersion) {
  if (!finalVersion || (!finalVersion.version_number && !finalVersion.id)) {
    return "No final BRD yet";
  }
  const parts = [];
  if (finalVersion.version_number != null) parts.push(`v${finalVersion.version_number}`);
  if (finalVersion.label) parts.push(finalVersion.label);
  return parts.join(" - ");
}

function fmtShort(n) {
  const v = Number(n || 0);
  if (v >= 1_000_000) return (v / 1_000_000).toFixed(2) + "M";
  if (v >= 1_000) return (v / 1_000).toFixed(0) + "K";
  return v.toString();
}

function usagePct(used, limit) {
  if (!limit || limit <= 0) return 0;
  return Math.min(100, Math.round((Number(used || 0) / Number(limit)) * 100));
}

function healthColor(pct) {
  if (pct >= 90) return "#E24B4A";
  if (pct >= 70) return "#EF9F27";
  return "#1D9E75";
}

// ─── Token Budget Cell ────────────────────────────────────────────────────────
function TokenBudgetCell({
  u,
  isDark,
  getTokenLimitValue,
  handleTokenLimitDraft,
  handleTokenLimitUpdate,
  tokenLimitUpdating,
}) {
  const inputDraft = getTokenLimitValue(u, "input_token_limit");
  const outputDraft = getTokenLimitValue(u, "output_token_limit");

  // Compute derived total from current draft values
  const derivedTotal = useMemo(() => {
    const i = normalizeTokenLimit(inputDraft);
    const o = normalizeTokenLimit(outputDraft);
    return i + o;
  }, [inputDraft, outputDraft]);

  const rows = [
    {
      key: "input_token_limit",
      usedKey: "input_tokens",
      remainingKey: "remaining_input_tokens",
      label: "Input",
      isTotal: false,
      accentColor: isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.02)",
      borderColor: "var(--ae-border)",
    },
    {
      key: "output_token_limit",
      usedKey: "output_tokens",
      remainingKey: "remaining_output_tokens",
      label: "Output",
      isTotal: false,
      accentColor: isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.02)",
      borderColor: "var(--ae-border)",
    },
    {
      key: "total_token_limit",
      usedKey: "total_tokens",
      remainingKey: "remaining_total_tokens",
      label: "Total",
      isTotal: true,
      accentColor: isDark ? "rgba(242,101,34,0.08)" : "rgba(242,101,34,0.06)",
      borderColor: "rgba(242,101,34,0.3)",
    },
  ];

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        p: "10px 12px",
        borderRadius: "12px",
        background: isDark ? "rgba(255,255,255,0.03)" : "rgba(248,250,252,0.9)",
        border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.06)"}`,
        minWidth: 270,
      }}
    >
      {rows.map(({ key, usedKey, remainingKey, label, isTotal, accentColor, borderColor }) => {
        const limitRaw = isTotal ? derivedTotal : normalizeTokenLimit(getTokenLimitValue(u, key));
        const used = Number(u[usedKey] || 0);
        const remaining = Math.max(0, limitRaw - used);
        const pct = usagePct(used, limitRaw);
        const barColor = healthColor(pct);
        const draftVal = isTotal
          ? (derivedTotal / 1_000_000).toFixed(2)
          : getTokenLimitValue(u, key);

        return (
          <React.Fragment key={key}>
            {isTotal && (
              <Box
                sx={{
                  height: "1px",
                  background: isDark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.08)",
                  mx: -0.5,
                  my: "2px",
                }}
              />
            )}

            {/* Label row */}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
              <Typography
                sx={{
                  fontSize: "10px",
                  fontWeight: 600,
                  letterSpacing: "0.5px",
                  textTransform: "uppercase",
                  color: isTotal ? "#F26522" : "text.secondary",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                {isTotal && (
                  <Box
                    component="span"
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      background: "#F26522",
                      display: "inline-block",
                      animation: "pulse 2s infinite",
                      "@keyframes pulse": {
                        "0%,100%": { opacity: 1, transform: "scale(1)" },
                        "50%": { opacity: 0.4, transform: "scale(0.8)" },
                      },
                    }}
                  />
                )}
                {label}
              </Typography>

              {/* Input field */}
              <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <Typography sx={{ fontSize: "10px", color: "text.disabled" }}>
                  {fmtShort(used)} /
                </Typography>
                <TextField
                  size="small"
                  type="number"
                  value={draftVal}
                  onChange={(e) => {
                    if (!isTotal) handleTokenLimitDraft(u.id, key, e.target.value);
                  }}
                  inputProps={{
                    min: 0,
                    step: 0.1,
                    readOnly: isTotal,
                    style: {
                      padding: "3px 6px",
                      fontSize: "12px",
                      textAlign: "right",
                      width: "62px",
                      cursor: isTotal ? "default" : "text",
                    },
                  }}
                  title={isTotal ? "Auto-calculated: Input + Output" : `Set ${label} limit in millions`}
                  sx={{
                    "& .MuiInputBase-root": {
                      height: 26,
                      borderRadius: "7px",
                      bgcolor: accentColor,
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      border: `1px solid ${borderColor}`,
                    },
                    "& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: isTotal ? "rgba(242,101,34,0.5)" : "rgba(100,160,255,0.4)",
                    },
                  }}
                />
                <Typography sx={{ fontSize: "10px", color: "text.disabled" }}>M</Typography>
              </Box>
            </Box>

            {/* Progress bar */}
            <Box
              sx={{
                height: "5px",
                bgcolor: isDark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.07)",
                borderRadius: "999px",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  height: "100%",
                  width: `${pct}%`,
                  background: barColor,
                  borderRadius: "999px",
                  transition: "width 0.8s cubic-bezier(0.4,0,0.2,1), background 0.4s",
                }}
              />
            </Box>

            {/* Meta row */}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography sx={{ fontSize: "10px", fontWeight: 600, color: barColor }}>
                {pct}% used
              </Typography>
              <Typography sx={{ fontSize: "10px", color: "text.disabled" }}>
                {fmtShort(remaining)} left
              </Typography>
            </Box>

            {/* Total auto-calc note */}
            {isTotal && (
              <Typography sx={{ fontSize: "10px", color: "text.disabled", fontStyle: "italic", textAlign: "right", mt: "-4px" }}>
                = {fmtShort(derivedTotal)} (input + output)
              </Typography>
            )}
          </React.Fragment>
        );
      })}

      {/* Hint + Save */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: "2px" }}>
        <Typography sx={{ fontSize: "10px", color: "text.disabled" }}>
          Set 0 to revoke access
        </Typography>
        <Button
          size="small"
          variant="outlined"
          onClick={() => handleTokenLimitUpdate(u)}
          disabled={tokenLimitUpdating[u.id]}
          startIcon={
            tokenLimitUpdating[u.id]
              ? <CircularProgress size={11} sx={{ color: "#F26522" }} />
              : <SaveIcon sx={{ fontSize: 13 }} />
          }
          sx={{
            height: 26,
            borderRadius: "8px",
            textTransform: "none",
            fontSize: "11px",
            borderColor: "rgba(242,101,34,0.45)",
            color: "#F26522",
            px: 1.5,
            "&:hover": {
              borderColor: "#F26522",
              background: "rgba(242,101,34,0.06)",
            },
          }}
        >
          Save
        </Button>
      </Box>
    </Box>
  );
}

const HeaderSearchField = ({ placeholder, value, onChange, isDark }) => (
  <TextField
    size="small"
    placeholder={placeholder}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    variant="standard"
    autoComplete="off"
    sx={{
      mt: 0.5,
      display: "block",
      width: "100%",
      "& .MuiInputBase-input": {
        fontSize: "11px",
        color: "text.secondary",
        padding: "2px 0 2px",
      },
      "& .MuiInput-underline:before": {
        borderBottomColor: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)",
      },
      "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
        borderBottomColor: "#F26522",
      },
      "& .MuiInput-underline:after": {
        borderBottomColor: "#F26522",
      },
    }}
  />
);

export default function Admin() {
  const { user, logout } = useAuth();
  const { mode } = useThemeMode();
  const isDark = mode === "dark";
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);

  const isSuperAdmin = Boolean(user?.roles?.includes("admin"));
  const isUserAdmin = Boolean(user?.roles?.includes("user_admin"));
  const hasAccess = isSuperAdmin || isUserAdmin || user?.roles?.includes("analytics_viewer") || user?.roles?.includes("SUPPORT_ADMIN");

  const [addUserOpen, setAddUserOpen] = useState(false);
  const [warningDismissed, setWarningDismissed] = useState(false);

  useEffect(() => {
    if (user && user.remaining_total_tokens !== undefined && user.remaining_total_tokens >= 20000) {
      setWarningDismissed(false);
    }
  }, [user?.remaining_total_tokens]);

  const [users, setUsers] = useState([]);
  const [userLoading, setUserLoading] = useState(true);
  const [userError, setUserError] = useState("");
  const [userSearch, setUserSearch] = useState(() => {
    try { return localStorage.getItem("admin_userSearch") || ""; } catch { return ""; }
  });
  const [userPage, setUserPage] = useState(0);
  const [userRowsPerPage, setUserRowsPerPage] = useState(8);

  const [brdProjects, setBrdProjects] = useState([]);
  const [brdLoading, setBrdLoading] = useState(false);
  const [brdError, setBrdError] = useState("");
  const [brdMessage, setBrdMessage] = useState("");
  const [brdLoadedOnce, setBrdLoadedOnce] = useState(false);
  const [brdPage, setBrdPage] = useState(0);
  const [brdRowsPerPage, setBrdRowsPerPage] = useState(8);
  const [purgeDays, setPurgeDays] = useState("30");
  const [sddBrdHistory, setSddBrdHistory] = useState([]);
  const [sddBrdLoading, setSddBrdLoading] = useState(false);
  const [sddBrdLoadedOnce, setSddBrdLoadedOnce] = useState(false);
  const [sddBrdPage, setSddBrdPage] = useState(0);
  const [sddBrdRowsPerPage, setSddBrdRowsPerPage] = useState(8);

  const [sddWfHistory, setSddWfHistory] = useState([]);
  const [sddWfLoading, setSddWfLoading] = useState(false);
  const [sddWfLoadedOnce, setSddWfLoadedOnce] = useState(false);
  const [sddWfPage, setSddWfPage] = useState(0);
  const [sddWfRowsPerPage, setSddWfRowsPerPage] = useState(8);
  const [testCaseHistory, setTestCaseHistory] = useState([]);
  const [testCaseLoading, setTestCaseLoading] = useState(false);
  const [testCaseLoadedOnce, setTestCaseLoadedOnce] = useState(false);
  const [testCasePage, setTestCasePage] = useState(0);
  const [testCaseRowsPerPage, setTestCaseRowsPerPage] = useState(8);
  const [passwordVisible, setPasswordVisible] = useState({});
  const [passwordInputs, setPasswordInputs] = useState({});
  const [passwordUpdating, setPasswordUpdating] = useState({});
  const [tokenLimitInputs, setTokenLimitInputs] = useState({});
  const [tokenLimitUpdating, setTokenLimitUpdating] = useState({});
  const [trainingHistory, setTrainingHistory] = useState([]);
  const [trainingLoading, setTrainingLoading] = useState(false);
  const [trainingLoadedOnce, setTrainingLoadedOnce] = useState(false);
  const [trainingPage, setTrainingPage] = useState(0);
  const [trainingRowsPerPage, setTrainingRowsPerPage] = useState(8);

  // CSM Intelligence tab state
  const [csmStatus, setCsmStatus] = useState(null);
  const [csmItems, setCsmItems] = useState([]);
  const [csmLoading, setCsmLoading] = useState(false);
  const [csmLoadedOnce, setCsmLoadedOnce] = useState(false);
  const [csmError, setCsmError] = useState("");
  const [csmMessage, setCsmMessage] = useState("");
  const [csmFullIngesting, setCsmFullIngesting] = useState(false);
  const [csmLiveIngesting, setCsmLiveIngesting] = useState(false);
  const [csmPage, setCsmPage] = useState(0);
  const [csmRowsPerPage, setCsmRowsPerPage] = useState(10);
  const [csmTypeFilter, setCsmTypeFilter] = useState("");
  const [csmIdFilter, setCsmIdFilter] = useState("");
  const [csmScheduler, setCsmScheduler] = useState({ interval_minutes: 30, scheduler_enabled: true, fetch_since: "", fetch_until: "", updated_at: null });
  const [csmSchedulerDraft, setCsmSchedulerDraft] = useState(null);
  const [csmSchedulerSaving, setCsmSchedulerSaving] = useState(false);
  const [csmInnerTab, setCsmInnerTab] = useState(0);
  const [csmSearchDate, setCsmSearchDate] = useState(() => new Date().toISOString().substring(0, 10));
  const [csmSearchResults, setCsmSearchResults] = useState([]);
  const [csmSearchLoading, setCsmSearchLoading] = useState(false);
  const [csmSearchError, setCsmSearchError] = useState("");
  const [csmTogglingItemId, setCsmTogglingItemId] = useState("");

  // CSM India Intelligence tab state
  const [csmIndiaStatus, setCsmIndiaStatus] = useState(null);
  const [csmIndiaItems, setCsmIndiaItems] = useState([]);
  const [csmIndiaLoading, setCsmIndiaLoading] = useState(false);
  const [csmIndiaLoadedOnce, setCsmIndiaLoadedOnce] = useState(false);
  const [csmIndiaError, setCsmIndiaError] = useState("");
  const [csmIndiaMessage, setCsmIndiaMessage] = useState("");
  const [csmIndiaLiveIngesting, setCsmIndiaLiveIngesting] = useState(false);
  const [csmIndiaPage, setCsmIndiaPage] = useState(0);
  const [csmIndiaRowsPerPage, setCsmIndiaRowsPerPage] = useState(10);
  const [csmIndiaMailboxFilter, setCsmIndiaMailboxFilter] = useState("");
  const [csmIndiaIdFilter, setCsmIndiaIdFilter] = useState("");
  const [csmIndiaScheduler, setCsmIndiaScheduler] = useState({ interval_minutes: 30, scheduler_enabled: true, fetch_since: "", fetch_until: "", updated_at: null });
  const [csmIndiaSchedulerDraft, setCsmIndiaSchedulerDraft] = useState(null);
  const [csmIndiaSchedulerSaving, setCsmIndiaSchedulerSaving] = useState(false);
  const [csmIndiaInnerTab, setCsmIndiaInnerTab] = useState(0);

  const [purging, setPurging] = useState(false);
  const [userColFilters, setUserColFilters] = useState(() => {
    try { return JSON.parse(localStorage.getItem("admin_userColFilters")) || { email: "", roles: "", status: "", creator: "" }; } catch { return { email: "", roles: "", status: "", creator: "" }; }
  });
  const [brdColFilters, setBrdColFilters] = useState(() => {
    try { return JSON.parse(localStorage.getItem("admin_brdColFilters")) || { name: "", creator: "", client: "", status: "" }; } catch { return { name: "", creator: "", client: "", status: "" }; }
  });
  const [sddBrdColFilters, setSddBrdColFilters] = useState(() => {
    try { return JSON.parse(localStorage.getItem("admin_sddBrdColFilters")) || { filename: "", user: "", email: "" }; } catch { return { filename: "", user: "", email: "" }; }
  });
  const [sddWfColFilters, setSddWfColFilters] = useState(() => {
    try { return JSON.parse(localStorage.getItem("admin_sddWfColFilters")) || { project: "", client: "", user: "", status: "" }; } catch { return { project: "", client: "", user: "", status: "" }; }
  });
  const [testCaseColFilters, setTestCaseColFilters] = useState(() => {
    try { return JSON.parse(localStorage.getItem("admin_testCaseColFilters")) || { project: "", email: "", user: "" }; } catch { return { project: "", email: "", user: "" }; }
  });
  const [trainingColFilters, setTrainingColFilters] = useState(() => {
    try { return JSON.parse(localStorage.getItem("admin_trainingColFilters")) || { trainee: "", module: "", email: "" }; } catch { return { trainee: "", module: "", email: "" }; }
  });

  useEffect(() => { try { localStorage.setItem("admin_userSearch", userSearch); } catch { } }, [userSearch]);
  useEffect(() => { try { localStorage.setItem("admin_userColFilters", JSON.stringify(userColFilters)); } catch { } }, [userColFilters]);
  useEffect(() => { try { localStorage.setItem("admin_brdColFilters", JSON.stringify(brdColFilters)); } catch { } }, [brdColFilters]);
  useEffect(() => { try { localStorage.setItem("admin_sddBrdColFilters", JSON.stringify(sddBrdColFilters)); } catch { } }, [sddBrdColFilters]);
  useEffect(() => { try { localStorage.setItem("admin_sddWfColFilters", JSON.stringify(sddWfColFilters)); } catch { } }, [sddWfColFilters]);
  useEffect(() => { try { localStorage.setItem("admin_testCaseColFilters", JSON.stringify(testCaseColFilters)); } catch { } }, [testCaseColFilters]);
  useEffect(() => { try { localStorage.setItem("admin_trainingColFilters", JSON.stringify(trainingColFilters)); } catch { } }, [trainingColFilters]);

  const headerBg = isDark ? "rgba(255,255,255,0.05)" : "#f8fafc";
  const glassBg = isDark ? "rgba(15, 23, 42, 0.8)" : "rgba(255, 255, 255, 0.94)";
  const glassBorder = isDark ? "rgba(255, 255, 255, 0.08)" : "rgba(15, 23, 42, 0.08)";

  const filteredUsers = useMemo(() => {
    const query = userSearch.trim().toLowerCase();
    // A user admin only manages users they created or approved; everyone else stays read-only in analytics.
    let result = isSuperAdmin ? users : users.filter((item) => item.can_manage);
    if (query) {
      result = result.filter((item) =>
        [item.email, item.name, item.display_name, toUiRoleLabel(item.roles), item.is_approved ? "approved" : "pending"]
          .filter(Boolean).join(" ").toLowerCase().includes(query)
      );
    }
    if (userColFilters.email) result = result.filter(u => (u.email || "").toLowerCase().includes(userColFilters.email.toLowerCase()));
    if (userColFilters.roles) result = result.filter(u => toUiRoleLabel(u.roles).toLowerCase().includes(userColFilters.roles.toLowerCase()));
    if (userColFilters.status) {
      const s = userColFilters.status.toLowerCase();
      result = result.filter(u => (u.is_approved ? "approved" : "pending").includes(s));
    }
    if (userColFilters.creator) {
      const c = userColFilters.creator.toLowerCase();
      result = result.filter(u => (u.created_by_email || "").toLowerCase().includes(c) || (u.created_by || "").toLowerCase().includes(c) || (u.approved_by_email || "").toLowerCase().includes(c) || (u.approved_by || "").toLowerCase().includes(c));
    }
    return [...result].sort((a, b) => (Number(b.total_tokens) || 0) - (Number(a.total_tokens) || 0));
  }, [users, userSearch, userColFilters, isSuperAdmin]);

  const filteredBrdProjects = useMemo(() => {
    let result = brdProjects;
    if (brdColFilters.name) result = result.filter(p => (p.name || "").toLowerCase().includes(brdColFilters.name.toLowerCase()));
    if (brdColFilters.creator) result = result.filter(p => (p.creator_email || "").toLowerCase().includes(brdColFilters.creator.toLowerCase()));
    if (brdColFilters.client) {
      const q = brdColFilters.client.toLowerCase();
      result = result.filter(p => (p.client_name || "").toLowerCase().includes(q) || (p.process_name || "").toLowerCase().includes(q));
    }
    if (brdColFilters.status) result = result.filter(p => (p.status || "draft").toLowerCase().includes(brdColFilters.status.toLowerCase()));
    return result;
  }, [brdProjects, brdColFilters]);

  const filteredSddBrdHistory = useMemo(() => {
    let result = sddBrdHistory;
    if (sddBrdColFilters.filename) result = result.filter(x => (x.filename || "").toLowerCase().includes(sddBrdColFilters.filename.toLowerCase()));
    if (sddBrdColFilters.user) result = result.filter(x => (x.username || "").toLowerCase().includes(sddBrdColFilters.user.toLowerCase()));
    if (sddBrdColFilters.email) result = result.filter(x => (x.email || "").toLowerCase().includes(sddBrdColFilters.email.toLowerCase()));
    return result;
  }, [sddBrdHistory, sddBrdColFilters]);

  const filteredSddWfHistory = useMemo(() => {
    let result = sddWfHistory;
    if (sddWfColFilters.project) result = result.filter(x => (x.project_name || x.file_name || "").toLowerCase().includes(sddWfColFilters.project.toLowerCase()));
    if (sddWfColFilters.client) result = result.filter(x => (x.client_name || "").toLowerCase().includes(sddWfColFilters.client.toLowerCase()));
    if (sddWfColFilters.user) result = result.filter(x => (x.user_name || "").toLowerCase().includes(sddWfColFilters.user.toLowerCase()));
    if (sddWfColFilters.status) result = result.filter(x => (x.status || "").toLowerCase().includes(sddWfColFilters.status.toLowerCase()));
    return result;
  }, [sddWfHistory, sddWfColFilters]);

  const filteredTestCaseHistory = useMemo(() => {
    let result = testCaseHistory;
    if (testCaseColFilters.project) result = result.filter(x => (x.project_name || "").toLowerCase().includes(testCaseColFilters.project.toLowerCase()));
    if (testCaseColFilters.email) result = result.filter(x => (x.user_email || "").toLowerCase().includes(testCaseColFilters.email.toLowerCase()));
    if (testCaseColFilters.user) result = result.filter(x => (x.user_id || "").toLowerCase().includes(testCaseColFilters.user.toLowerCase()));
    return result;
  }, [testCaseHistory, testCaseColFilters]);

  const filteredTrainingHistory = useMemo(() => {
    let result = trainingHistory;
    if (trainingColFilters.trainee) result = result.filter(x => (x.trainee_name || "").toLowerCase().includes(trainingColFilters.trainee.toLowerCase()));
    if (trainingColFilters.module) result = result.filter(x => (x.trainee_module || "").toLowerCase().includes(trainingColFilters.module.toLowerCase()));
    if (trainingColFilters.email) result = result.filter(x => (x.email || "").toLowerCase().includes(trainingColFilters.email.toLowerCase()));
    return result;
  }, [trainingHistory, trainingColFilters]);

  const pagedUsers = useMemo(() => {
    const start = userPage * userRowsPerPage;
    return filteredUsers.slice(start, start + userRowsPerPage);
  }, [filteredUsers, userPage, userRowsPerPage]);

  const pagedBrdProjects = useMemo(() => {
    const start = brdPage * brdRowsPerPage;
    return filteredBrdProjects.slice(start, start + brdRowsPerPage);
  }, [filteredBrdProjects, brdPage, brdRowsPerPage]);

  const getRolesForUser = (heldRoles) => getAssignableRoles({ isSuperAdmin, isUserAdmin, heldRoles });

  const handleOpenAddUser = () => setAddUserOpen(true);

  const fetchUsers = useCallback(async () => {
    setUserLoading(true); setUserError("");
    try {
      const token = localStorage.getItem("edge_token");
      const res = await axios.get("/api/admin/users", { headers: { Authorization: `Bearer ${token}` } });
      setUsers(res.data);
    } catch (err) {
      console.error(err);
      setUserError("Failed to fetch users. Ensure you have Admin privileges.");
    } finally { setUserLoading(false); }
  }, []);

  const fetchBrdProjects = useCallback(async () => {
    setBrdLoading(true); setBrdError(""); setBrdMessage("");
    try {
      const token = localStorage.getItem("edge_token");
      const res = await axios.get("/api/admin/brd/projects", { headers: { Authorization: `Bearer ${token}` } });
      setBrdProjects(res.data || []);
    } catch (err) {
      console.error(err);
      setBrdError("Failed to fetch BRD projects. Ensure you have Admin privileges.");
    } finally { setBrdLoading(false); setBrdLoadedOnce(true); }
  }, []);

  const fetchSddBrdHistory = useCallback(async () => {
    setSddBrdLoading(true); setSddBrdLoadedOnce(true);
    try {
      const token = localStorage.getItem("edge_token");
      const res = await axios.get("/api/admin/sdd/brd/history", { headers: { Authorization: `Bearer ${token}` } });
      setSddBrdHistory(res.data || []);
    } catch (err) { console.error(err); setBrdError("Failed to fetch SDD BRD history."); }
    finally { setSddBrdLoading(false); }
  }, []);

  const fetchSddWfHistory = useCallback(async () => {
    setSddWfLoading(true); setSddWfLoadedOnce(true);
    try {
      const token = localStorage.getItem("edge_token");
      const res = await axios.get("/api/admin/sdd/workflow/history", { headers: { Authorization: `Bearer ${token}` } });
      setSddWfHistory(res.data || []);
    } catch (err) { console.error(err); setBrdError("Failed to fetch SDD Workflow history."); }
    finally { setSddWfLoading(false); }
  }, []);

  const fetchTestCaseHistory = useCallback(async () => {
    setTestCaseLoading(true); setTestCaseLoadedOnce(true);
    try {
      const token = localStorage.getItem("edge_token");
      const res = await axios.get("/api/admin/test-case/history", { headers: { Authorization: `Bearer ${token}` } });
      setTestCaseHistory(res.data || []);
    } catch (err) { console.error(err); setBrdError("Failed to fetch Test Case history."); }
    finally { setTestCaseLoading(false); }
  }, []);

  const fetchTrainingHistory = useCallback(async () => {
    setTrainingLoading(true); setTrainingLoadedOnce(true);
    try {
      const token = localStorage.getItem("edge_token");
      const res = await axios.get("/api/admin/training/history", { headers: { Authorization: `Bearer ${token}` } });
      setTrainingHistory(res.data || []);
    } catch (err) { console.error(err); setBrdError("Failed to fetch Training history."); }
    finally { setTrainingLoading(false); }
  }, []);

  const fetchCsmData = useCallback(async () => {
    setCsmLoading(true); setCsmError(""); setCsmLoadedOnce(true);
    try {
      const token = localStorage.getItem("edge_token");
      const headers = { Authorization: `Bearer ${token}` };
      const [statusRes, itemsRes, schedulerRes] = await Promise.all([
        axios.get("/api/admin/csm/status", { headers }),
        axios.get("/api/admin/csm/processed-items", { headers }),
        axios.get("/api/admin/csm/scheduler/settings", { headers }),
      ]);
      setCsmStatus(statusRes.data);
      setCsmItems(itemsRes.data || []);
      const s = schedulerRes.data;
      setCsmScheduler(s);
      setCsmSchedulerDraft({ interval_minutes: s.interval_minutes, scheduler_enabled: s.scheduler_enabled, fetch_since: s.fetch_since || "", fetch_until: s.fetch_until || "" });
    } catch (err) {
      console.error(err);
      setCsmError("Failed to load CSM data.");
    } finally { setCsmLoading(false); }
  }, []);

  const fetchCsmIndiaData = useCallback(async () => {
    setCsmIndiaLoading(true); setCsmIndiaError(""); setCsmIndiaLoadedOnce(true);
    try {
      const token = localStorage.getItem("edge_token");
      const headers = { Authorization: `Bearer ${token}` };
      const [statusRes, itemsRes, schedulerRes] = await Promise.all([
        axios.get("/api/admin/csm/india/status", { headers }),
        axios.get("/api/admin/csm/india/processed-items", { headers }),
        axios.get("/api/admin/csm/india/scheduler/settings", { headers }),
      ]);
      setCsmIndiaStatus(statusRes.data);
      setCsmIndiaItems(itemsRes.data || []);
      const s = schedulerRes.data;
      setCsmIndiaScheduler(s);
      setCsmIndiaSchedulerDraft({ interval_minutes: s.interval_minutes, scheduler_enabled: s.scheduler_enabled, fetch_since: s.fetch_since || "", fetch_until: s.fetch_until || "" });
    } catch (err) {
      console.error(err);
      setCsmIndiaError("Failed to load CSM India data.");
    } finally { setCsmIndiaLoading(false); }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  useEffect(() => {
    if (tab === 1 && !brdLoadedOnce && !brdLoading) fetchBrdProjects();
    if (tab === 2 && !sddBrdLoadedOnce && !sddBrdLoading) fetchSddBrdHistory();
    if (tab === 3 && !sddWfLoadedOnce && !sddWfLoading) fetchSddWfHistory();
    if (tab === 4 && !testCaseLoadedOnce && !testCaseLoading) fetchTestCaseHistory();
    if (tab === 5 && !trainingLoadedOnce && !trainingLoading) fetchTrainingHistory();
    if (tab === 6 && !csmLoadedOnce && !csmLoading) fetchCsmData();
    if (tab === 7 && !csmIndiaLoadedOnce && !csmIndiaLoading) fetchCsmIndiaData();
  }, [tab, brdLoadedOnce, brdLoading, fetchBrdProjects, sddBrdLoadedOnce, sddBrdLoading, fetchSddBrdHistory, sddWfLoadedOnce, sddWfLoading, fetchSddWfHistory, testCaseLoadedOnce, testCaseLoading, fetchTestCaseHistory, trainingLoadedOnce, trainingLoading, fetchTrainingHistory, csmLoadedOnce, csmLoading, fetchCsmData, csmIndiaLoadedOnce, csmIndiaLoading, fetchCsmIndiaData]);

  const handlePurgeTestCase = async (filename) => {
    if (!window.confirm(`Purge test case file "${filename}"?`)) return;
    try {
      const token = localStorage.getItem("edge_token");
      await axios.delete(`/api/admin/test-case/history/${filename}`, { headers: { Authorization: `Bearer ${token}` } });
      setTestCaseHistory(prev => prev.filter(x => x.filename !== filename));
    } catch (err) { console.error(err); setBrdError("Failed to purge test case file."); }
  };

  const handleBulkPurgeTestCases = async () => {
    const days = parseInt(purgeDays, 10);
    if (!days) return;
    if (!window.confirm(`Purge every test case file older than ${days} days?`)) return;
    setPurging(true);
    try {
      const token = localStorage.getItem("edge_token");
      await axios.post("/api/admin/test-case/history/purge", { older_than_days: days }, { headers: { Authorization: `Bearer ${token}` } });
      fetchTestCaseHistory();
    } catch (err) { console.error(err); } finally { setPurging(false); }
  };

  const handleDownloadTestCase = async (filename) => {
    try {
      const token = localStorage.getItem("edge_token");
      const res = await axios.get(`/api/test-case/download/${filename}`, { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url; link.setAttribute("download", filename);
      document.body.appendChild(link); link.click(); link.remove();
    } catch (err) { console.error(err); setBrdError("Failed to download file."); }
  };

  const handleRolesChange = async (userId, newRoles) => {
    try {
      const token = localStorage.getItem("edge_token");
      const normalizedRoles = Array.isArray(newRoles) ? [...new Set(newRoles)] : [newRoles];
      await axios.put(`/api/admin/users/${userId}/roles`, { roles: normalizedRoles }, { headers: { Authorization: `Bearer ${token}` } });
      setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, roles: normalizedRoles } : u)));
    } catch (err) { console.error(err); setUserError("Failed to update roles."); }
  };

  const handleApprove = async (userId) => {
    try {
      const token = localStorage.getItem("edge_token");
      await axios.put(`/api/admin/users/${userId}/approve`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, is_approved: true } : u)));
    } catch (err) { console.error(err); setUserError("Failed to approve user."); }
  };

  const handleSessionLimitChange = async (userId, maxSessions) => {
    try {
      const token = localStorage.getItem("edge_token");
      await axios.put(`/api/admin/users/${userId}/session-limit`, { max_sessions: maxSessions }, { headers: { Authorization: `Bearer ${token}` } });
      setUsers((prev) => prev.map((u) => u.id === userId ? { ...u, max_sessions: maxSessions, active_sessions: Math.min(Number(u.active_sessions || 0), maxSessions) } : u));
    } catch (err) { console.error(err); setUserError("Failed to update session limit."); }
  };

  const getTokenLimitValue = (u, key) => {
    if (tokenLimitInputs[u.id]?.[key] !== undefined) return tokenLimitInputs[u.id][key];
    const raw = Number(u[key] ?? 0);
    return raw === 0 ? "0" : (raw / 1_000_000).toFixed(2);
  };

  const handleTokenLimitDraft = (userId, key, value) => {
    setTokenLimitInputs((prev) => ({
      ...prev,
      [userId]: { ...(prev[userId] || {}), [key]: value },
    }));
  };

  const handleTokenLimitUpdate = async (u) => {
    const inputLimit = normalizeTokenLimit(getTokenLimitValue(u, "input_token_limit"));
    const outputLimit = normalizeTokenLimit(getTokenLimitValue(u, "output_token_limit"));
    const payload = {
      input_token_limit: inputLimit,
      output_token_limit: outputLimit,
      total_token_limit: inputLimit + outputLimit,
    };
    setTokenLimitUpdating((prev) => ({ ...prev, [u.id]: true }));
    try {
      const token = localStorage.getItem("edge_token");
      const res = await axios.put(`/api/admin/users/${u.id}/token-limit`, payload, { headers: { Authorization: `Bearer ${token}` } });
      setUsers((prev) => prev.map((item) => (item.id === u.id ? { ...item, ...res.data } : item)));
      setTokenLimitInputs((prev) => { const next = { ...prev }; delete next[u.id]; return next; });
    } catch (err) { console.error(err); setUserError("Failed to update token limit."); }
    finally { setTokenLimitUpdating((prev) => ({ ...prev, [u.id]: false })); }
  };

  const handlePasswordUpdate = async (userId) => {
    const newPwd = passwordInputs[userId];
    if (!newPwd) return;
    setPasswordUpdating(prev => ({ ...prev, [userId]: true }));
    try {
      const token = localStorage.getItem("edge_token");
      await axios.put(`/api/admin/users/${userId}/password`, { password: newPwd }, { headers: { Authorization: `Bearer ${token}` } });
      setPasswordInputs(prev => ({ ...prev, [userId]: "" }));
      alert("Password updated successfully!");
    } catch (err) { console.error(err); setUserError("Failed to update password."); }
    finally { setPasswordUpdating(prev => ({ ...prev, [userId]: false })); }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm("Are you sure you want to delete this user? This action cannot be undone.")) return;
    try {
      const token = localStorage.getItem("edge_token");
      await axios.delete(`/api/admin/users/${userId}`, { headers: { Authorization: `Bearer ${token}` } });
      setUsers((prev) => prev.filter((u) => u.id !== userId));
    } catch (err) { console.error(err); setUserError(getApiErrorMessage(err, "Failed to delete user.")); }
  };

  const handlePurgeProject = async (project) => {
    if (!window.confirm(`Permanently purge "${project.name}"? This removes the BRD project, versions, documents, captures, exports, and local files.`)) return;
    try {
      const token = localStorage.getItem("edge_token");
      await axios.delete(`/api/admin/brd/projects/${project.id}`, { headers: { Authorization: `Bearer ${token}` } });
      setBrdProjects((prev) => prev.filter((item) => item.id !== project.id));
      setBrdMessage(`Purged ${project.name}.`); setBrdPage(0);
    } catch (err) { console.error(err); setBrdError("Failed to purge BRD project."); }
  };

  const handlePurgeSddBrd = async (id) => {
    if (!window.confirm("Purge this SDD record?")) return;
    try {
      const token = localStorage.getItem("edge_token");
      await axios.delete(`/api/admin/sdd/brd/history/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setSddBrdHistory(prev => prev.filter(x => x.id !== id));
    } catch (err) { console.error(err); }
  };

  const handlePurgeSddWf = async (id) => {
    if (!window.confirm("Purge this Workflow SDD record?")) return;
    try {
      const token = localStorage.getItem("edge_token");
      await axios.delete(`/api/admin/sdd/workflow/history/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setSddWfHistory(prev => prev.filter(x => x.id !== id));
    } catch (err) { console.error(err); }
  };

  const handleBulkPurgeSdd = async (mode) => {
    const days = parseInt(purgeDays, 10);
    if (!days) return;
    if (!window.confirm(`Purge ${mode} records older than ${days} days?`)) return;
    setPurging(true);
    try {
      const token = localStorage.getItem("edge_token");
      const endpoint = mode === "brd" ? "/api/admin/sdd/brd/history/purge" : "/api/admin/sdd/workflow/history/purge";
      await axios.post(endpoint, { older_than_days: days }, { headers: { Authorization: `Bearer ${token}` } });
      if (mode === "brd") fetchSddBrdHistory(); else fetchSddWfHistory();
    } catch (err) { console.error(err); } finally { setPurging(false); }
  };

  const handlePurgeTraining = async (id) => {
    if (!window.confirm("Purge this Training Evaluation record?")) return;
    try {
      const token = localStorage.getItem("edge_token");
      await axios.delete(`/api/admin/training/history/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setTrainingHistory(prev => prev.filter(x => x.id !== id));
    } catch (err) { console.error(err); setBrdError("Failed to purge record."); }
  };

  const handleBulkPurge = async () => {
    const days = parseInt(purgeDays, 10);
    if (!Number.isFinite(days) || days < 1) { setBrdError("Enter a valid number of days."); return; }
    if (!window.confirm(`Purge every BRD project older than ${days} days? This permanently deletes the matching BRD data and files.`)) return;
    setPurging(true); setBrdError(""); setBrdMessage("");
    try {
      const token = localStorage.getItem("edge_token");
      const res = await axios.post("/api/admin/brd/projects/purge", { older_than_days: days }, { headers: { Authorization: `Bearer ${token}` } });
      const deleted = Number(res.data?.deleted || 0);
      setBrdMessage(deleted > 0 ? `Purged ${deleted} BRD project${deleted === 1 ? "" : "s"} older than ${days} days.` : `No BRD projects were older than ${days} days.`);
      await fetchBrdProjects(); setBrdPage(0);
    } catch (err) { console.error(err); setBrdError("Failed to purge old BRD projects."); }
    finally { setPurging(false); }
  };

  if (!user) return <Navigate to="/login" replace />;

  if (!isSuperAdmin && !isUserAdmin) {
    return (
      <Box sx={{ p: 4, textAlign: "center", color: "#8fa3c0" }}>
        <Typography variant="h5">Access Denied: Administrators only.</Typography>
        <Button variant="outlined" onClick={() => navigate("/")} sx={{ mt: 3, borderColor: "var(--ae-border)", color: "text.secondary" }}>
          Go to Dashboard
        </Button>
      </Box>
    );
  }

  const renderUserTable = () => (
    <Paper
      elevation={0}
      sx={{
        background: glassBg, backdropFilter: "blur(12px)", borderRadius: "24px",
        border: `1px solid ${glassBorder}`,
        boxShadow: isDark ? "0 12px 40px rgba(0,0,0,0.4)" : "0 10px 30px rgba(15,23,42,0.06)",
        display: "flex", flexDirection: "column", flex: 1, minHeight: 0, overflow: "hidden",
      }}
    >
      <TableContainer sx={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "auto" }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: "text.primary", fontWeight: "bold", bgcolor: headerBg, whiteSpace: "nowrap", position: "sticky", top: 0, zIndex: 2 }}>
                Email
                <HeaderSearchField placeholder="Search Email" value={userColFilters.email} onChange={(v) => setUserColFilters(p => ({ ...p, email: v }))} isDark={isDark} />
              </TableCell>
              <TableCell sx={{ color: "text.primary", fontWeight: "bold", bgcolor: headerBg, whiteSpace: "nowrap", position: "sticky", top: 0, zIndex: 2 }}>
                Created / Approved By
                <HeaderSearchField placeholder="Search Creator / Approver" value={userColFilters.creator} onChange={(v) => setUserColFilters(p => ({ ...p, creator: v }))} isDark={isDark} />
              </TableCell>
              <TableCell sx={{ color: "text.primary", fontWeight: "bold", bgcolor: headerBg, whiteSpace: "nowrap", position: "sticky", top: 0, zIndex: 2 }}>
                Current Roles
                <HeaderSearchField placeholder="Search Roles" value={userColFilters.roles} onChange={(v) => setUserColFilters(p => ({ ...p, roles: v }))} isDark={isDark} />
              </TableCell>
              <TableCell sx={{ color: "text.primary", fontWeight: "bold", bgcolor: headerBg, whiteSpace: "nowrap", position: "sticky", top: 0, zIndex: 2 }}>
                Update Password
              </TableCell>
              <TableCell sx={{ color: "text.primary", fontWeight: "bold", bgcolor: headerBg, whiteSpace: "nowrap", position: "sticky", top: 0, zIndex: 2 }}>
                Status
                <HeaderSearchField placeholder="Search Status" value={userColFilters.status} onChange={(v) => setUserColFilters(p => ({ ...p, status: v }))} isDark={isDark} />
              </TableCell>
              <TableCell sx={{ color: "text.primary", fontWeight: "bold", bgcolor: headerBg, whiteSpace: "nowrap", minWidth: 130, position: "sticky", top: 0, zIndex: 2 }}>Sessions</TableCell>
              <TableCell sx={{ color: "text.primary", fontWeight: "bold", bgcolor: headerBg, whiteSpace: "nowrap", minWidth: 90, position: "sticky", top: 0, zIndex: 2 }}>Input</TableCell>
              <TableCell sx={{ color: "text.primary", fontWeight: "bold", bgcolor: headerBg, whiteSpace: "nowrap", minWidth: 90, position: "sticky", top: 0, zIndex: 2 }}>Output</TableCell>
              <TableCell sx={{ color: "text.primary", fontWeight: "bold", bgcolor: headerBg, whiteSpace: "nowrap", minWidth: 90, position: "sticky", top: 0, zIndex: 2 }}>Total</TableCell>
              <TableCell sx={{ color: "text.primary", fontWeight: "bold", bgcolor: headerBg, whiteSpace: "nowrap", minWidth: 300, position: "sticky", top: 0, zIndex: 2 }}>
                Token Budget
              </TableCell>
              <TableCell sx={{ color: "text.primary", fontWeight: "bold", bgcolor: headerBg, whiteSpace: "nowrap", minWidth: 220, position: "sticky", top: 0, zIndex: 2 }}>Service Usage</TableCell>
              <TableCell sx={{ color: "text.primary", fontWeight: "bold", bgcolor: headerBg, whiteSpace: "nowrap", minWidth: 260, position: "sticky", top: 0, zIndex: 2 }}>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pagedUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={12} sx={{ color: "text.secondary", py: 4, textAlign: "center" }}>No users found.</TableCell>
              </TableRow>
            ) : (
              pagedUsers.map((u) => (
                <TableRow key={u.id} sx={{ "&:last-child td, &:last-child th": { border: 0 }, "&:hover": { bgcolor: "var(--ae-surface)" } }}>
                  <TableCell sx={{ color: "text.secondary", verticalAlign: "top" }}>{u.email}</TableCell>
                  <TableCell sx={{ color: "text.secondary", verticalAlign: "top", whiteSpace: "nowrap" }}>
                    {u.created_by ? (
                      <Chip
                        size="small"
                        label={u.created_by === user?.id ? "Created by You" : (u.created_by_email || u.created_by.substring(0, 8))}
                        sx={{
                          fontSize: "11px",
                          height: 22,
                          bgcolor: u.created_by === user?.id ? "rgba(242,101,34,0.12)" : (isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)"),
                          color: u.created_by === user?.id ? "#F26522" : "text.secondary",
                          border: "1px solid",
                          borderColor: u.created_by === user?.id ? "rgba(242,101,34,0.3)" : (isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"),
                        }}
                      />
                    ) : u.approved_by ? (
                      <Chip
                        size="small"
                        label={u.approved_by === user?.id ? "Approved by You" : (u.approved_by_email || u.approved_by.substring(0, 8))}
                        sx={{
                          fontSize: "11px",
                          height: 22,
                          bgcolor: u.approved_by === user?.id ? "rgba(34,197,94,0.12)" : (isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)"),
                          color: u.approved_by === user?.id ? "#22c55e" : "text.secondary",
                          border: "1px solid",
                          borderColor: u.approved_by === user?.id ? "rgba(34,197,94,0.3)" : (isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"),
                        }}
                      />
                    ) : (
                      <Typography sx={{ color: "text.disabled", fontSize: 12 }}>Self Registered (Pending)</Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ color: "text.secondary", verticalAlign: "top", whiteSpace: "normal", wordBreak: "break-word", maxWidth: 320 }}>
                    {toUiRoleLabel(u.roles)}
                  </TableCell>
                  <TableCell sx={{ verticalAlign: "top" }}>
                    <TextField
                      size="small"
                      placeholder="New password"
                      type={passwordVisible[u.id] ? "text" : "password"}
                      value={passwordInputs[u.id] || ""}
                      onChange={(e) => setPasswordInputs(prev => ({ ...prev, [u.id]: e.target.value }))}
                      onKeyDown={(e) => { if (e.key === "Enter") handlePasswordUpdate(u.id); }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton size="small" onClick={() => setPasswordVisible(prev => ({ ...prev, [u.id]: !prev[u.id] }))}>
                              {passwordVisible[u.id] ? <VisibilityOff sx={{ fontSize: 16 }} /> : <Visibility sx={{ fontSize: 16 }} />}
                            </IconButton>
                            {passwordInputs[u.id] && (
                              <Tooltip title="Save Password">
                                <IconButton size="small" onClick={() => handlePasswordUpdate(u.id)} disabled={passwordUpdating[u.id]} sx={{ color: "#F26522" }}>
                                  {passwordUpdating[u.id] ? <CircularProgress size={16} sx={{ color: "#F26522" }} /> : <SaveIcon sx={{ fontSize: 16 }} />}
                                </IconButton>
                              </Tooltip>
                            )}
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        width: 140,
                        "& .MuiInputBase-root": { height: 32, fontSize: 12, bgcolor: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)", borderRadius: "8px" },
                        "& .MuiOutlinedInput-notchedOutline": { border: "1px solid var(--ae-border)" },
                        "& .MuiInputBase-input::placeholder": { fontSize: "10px", opacity: 0.5 },
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ color: u.is_approved ? "#4caf50" : "#ff9800", fontWeight: "bold", verticalAlign: "top", whiteSpace: "nowrap" }}>
                    {u.is_approved ? "Approved" : "Pending"}
                  </TableCell>
                  <TableCell sx={{ verticalAlign: "top", whiteSpace: "nowrap" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Select
                        size="small"
                        value={Number(u.max_sessions || 1)}
                        onChange={(e) => handleSessionLimitChange(u.id, Number(e.target.value))}
                        sx={{ color: "text.primary", ".MuiOutlinedInput-notchedOutline": { borderColor: "var(--ae-border)" }, minWidth: 72 }}
                      >
                        {[1, 2].map((value) => (<MenuItem key={value} value={value}>{value}</MenuItem>))}
                      </Select>
                      <Typography sx={{ color: "text.disabled", fontSize: 12 }}>{Number(u.active_sessions || 0)} active</Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ color: "text.secondary", verticalAlign: "top", whiteSpace: "nowrap" }}>{formatTokens(u.input_tokens)}</TableCell>
                  <TableCell sx={{ color: "text.secondary", verticalAlign: "top", whiteSpace: "nowrap" }}>{formatTokens(u.output_tokens)}</TableCell>
                  <TableCell sx={{ color: "#F26522", fontWeight: "bold", verticalAlign: "top", whiteSpace: "nowrap" }}>{formatTokens(u.total_tokens)}</TableCell>

                  {/* ── NEW Token Budget Cell ── */}
                  <TableCell sx={{ verticalAlign: "top", py: 1 }}>
                    <TokenBudgetCell
                      u={u}
                      isDark={isDark}
                      getTokenLimitValue={getTokenLimitValue}
                      handleTokenLimitDraft={handleTokenLimitDraft}
                      handleTokenLimitUpdate={handleTokenLimitUpdate}
                      tokenLimitUpdating={tokenLimitUpdating}
                    />
                  </TableCell>

                  <TableCell sx={{ color: "text.secondary", verticalAlign: "top" }}>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                      {(u.service_tokens || []).length > 0 ? (
                        u.service_tokens.map((svc) => (
                          <Chip
                            key={`${u.id}-${svc.service_name}`}
                            size="small"
                            label={`${svc.service_name}: ${formatTokens(svc.total_tokens)}`}
                            sx={{ bgcolor: "rgba(242,101,34,0.12)", color: "text.primary", border: "1px solid rgba(242,101,34,0.25)" }}
                          />
                        ))
                      ) : (
                        <Typography sx={{ color: "text.disabled", fontSize: 12 }}>No usage yet</Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ verticalAlign: "top" }}>
                    <Box sx={{ display: "flex", gap: "8px", alignItems: "flex-start", flexWrap: "wrap" }}>
                      <Select
                        multiple
                        value={u.roles || []}
                        size="small"
                        onChange={(e) => handleRolesChange(u.id, typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value)}
                        renderValue={(selected) => selected.map((role) => ROLE_LABELS[role] || role).join(", ")}
                        MenuProps={ROLE_SELECT_MENU_PROPS}
                        sx={{ color: "text.primary", ".MuiOutlinedInput-notchedOutline": { borderColor: "var(--ae-border)" }, minWidth: "220px", maxWidth: "340px" }}
                      >
                        {getRolesForUser(u.roles || []).map((role) => (<MenuItem key={role.value} value={role.value}>{role.label}</MenuItem>))}
                      </Select>
                      {!u.is_approved && (
                        <Button variant="contained" size="small" onClick={() => handleApprove(u.id)} sx={{ background: "#4caf50", "&:hover": { background: "#388e3c" } }}>
                          Approve
                        </Button>
                      )}
                      <Button variant="contained" size="small" onClick={() => handleDelete(u.id)} sx={{ background: "#f44336", "&:hover": { background: "#d32f2f" } }}>
                        Delete
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div" count={filteredUsers.length} page={userPage}
        onPageChange={(_, nextPage) => setUserPage(nextPage)}
        rowsPerPage={userRowsPerPage}
        onRowsPerPageChange={(e) => { setUserRowsPerPage(parseInt(e.target.value, 10)); setUserPage(0); }}
        rowsPerPageOptions={[8, 12, 16]}
        sx={{ color: "text.secondary", borderTop: "1px solid var(--ae-border)", flexShrink: 0 }}
      />
    </Paper>
  );

  const renderBrdTable = () => (
    <Paper elevation={0} sx={{ background: glassBg, backdropFilter: "blur(12px)", borderRadius: "24px", border: `1px solid ${glassBorder}`, boxShadow: isDark ? "0 12px 40px rgba(0,0,0,0.4)" : "0 10px 30px rgba(15,23,42,0.06)", display: "flex", flexDirection: "column", flex: 1, minHeight: 0, overflow: "hidden" }}>
      <Box sx={{ px: 2, py: 1.5, display: "flex", alignItems: { xs: "stretch", md: "center" }, justifyContent: "space-between", gap: 2, flexWrap: "wrap", borderBottom: "1px solid var(--ae-border)" }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" sx={{ color: "text.primary", fontWeight: 700 }}>BRD Admin</Typography>
          <Typography sx={{ color: "text.secondary", fontSize: 13 }}>Hard purge removes the project, its versions, captures, videos, documents, and local storage files.</Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
          <TextField size="small" type="number" label="Purge older than days" value={purgeDays} onChange={(e) => setPurgeDays(e.target.value)} inputProps={{ min: 1, max: 3650 }} sx={{ width: 190, "& .MuiInputBase-root": { color: "text.primary" } }} />
          <Button variant="contained" onClick={handleBulkPurge} disabled={purging} startIcon={<DeleteForeverIcon sx={{ fontSize: 18 }} />} sx={{ background: "#f44336", "&:hover": { background: "#d32f2f" } }}>
            {purging ? "Purging..." : "Purge Old Projects"}
          </Button>
          <Button variant="outlined" onClick={fetchBrdProjects} startIcon={<RefreshIcon sx={{ fontSize: 18 }} />} sx={{ borderColor: "var(--ae-border)", color: "text.secondary" }}>Refresh</Button>
        </Box>
      </Box>
      <TableContainer sx={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "auto" }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: "text.primary", fontWeight: "bold", bgcolor: headerBg, whiteSpace: "nowrap", position: "sticky", top: 0, zIndex: 2 }}>
                Project
                <HeaderSearchField placeholder="Search Project" value={brdColFilters.name} onChange={(v) => setBrdColFilters(p => ({ ...p, name: v }))} isDark={isDark} />
              </TableCell>
              <TableCell sx={{ color: "text.primary", fontWeight: "bold", bgcolor: headerBg, whiteSpace: "nowrap", minWidth: 220, position: "sticky", top: 0, zIndex: 2 }}>
                Created / Approved By
                <HeaderSearchField placeholder="Search Creator" value={brdColFilters.creator} onChange={(v) => setBrdColFilters(p => ({ ...p, creator: v }))} isDark={isDark} />
              </TableCell>
              <TableCell sx={{ color: "text.primary", fontWeight: "bold", bgcolor: headerBg, whiteSpace: "nowrap", minWidth: 260, position: "sticky", top: 0, zIndex: 2 }}>
                Client / Process
                <HeaderSearchField placeholder="Search Client/Process" value={brdColFilters.client} onChange={(v) => setBrdColFilters(p => ({ ...p, client: v }))} isDark={isDark} />
              </TableCell>
              <TableCell sx={{ color: "text.primary", fontWeight: "bold", bgcolor: headerBg, whiteSpace: "nowrap", position: "sticky", top: 0, zIndex: 2 }}>Created</TableCell>
              <TableCell sx={{ color: "text.primary", fontWeight: "bold", bgcolor: headerBg, whiteSpace: "nowrap", position: "sticky", top: 0, zIndex: 2 }}>Updated</TableCell>
              <TableCell sx={{ color: "text.primary", fontWeight: "bold", bgcolor: headerBg, whiteSpace: "nowrap", position: "sticky", top: 0, zIndex: 2 }}>
                Status
                <HeaderSearchField placeholder="Search Status" value={brdColFilters.status} onChange={(v) => setBrdColFilters(p => ({ ...p, status: v }))} isDark={isDark} />
              </TableCell>
              <TableCell sx={{ color: "text.primary", fontWeight: "bold", bgcolor: headerBg, whiteSpace: "nowrap", minWidth: 280, position: "sticky", top: 0, zIndex: 2 }}>Counts</TableCell>
              <TableCell sx={{ color: "text.primary", fontWeight: "bold", bgcolor: headerBg, whiteSpace: "nowrap", minWidth: 320, position: "sticky", top: 0, zIndex: 2 }}>Final BRD</TableCell>
              <TableCell sx={{ color: "text.primary", fontWeight: "bold", bgcolor: headerBg, whiteSpace: "nowrap", minWidth: 140, position: "sticky", top: 0, zIndex: 2 }}>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pagedBrdProjects.length === 0 ? (
              <TableRow><TableCell colSpan={9} sx={{ color: "text.secondary", py: 4, textAlign: "center" }}>No BRD projects found.</TableCell></TableRow>
            ) : (
              pagedBrdProjects.map((project) => (
                <TableRow key={project.id} sx={{ "&:last-child td, &:last-child th": { border: 0 }, "&:hover": { bgcolor: "var(--ae-surface)" } }}>
                  <TableCell sx={{ color: "text.secondary", verticalAlign: "top", minWidth: 220 }}>
                    <Typography sx={{ color: "text.primary", fontWeight: 700 }}>{project.name}</Typography>
                    <Typography sx={{ color: "text.disabled", fontSize: 12 }}>{project.template || "standard"}</Typography>
                  </TableCell>
                  <TableCell sx={{ color: "text.secondary", verticalAlign: "top" }}>
                    <Typography sx={{ color: "text.primary" }}>{project.creator_email || "-"}</Typography>
                    <Typography sx={{ color: "text.disabled", fontSize: 12 }}>{project.creator_display || project.creator_id || "-"}</Typography>
                  </TableCell>
                  <TableCell sx={{ color: "text.secondary", verticalAlign: "top" }}>
                    <Typography sx={{ color: "text.primary" }}>{project.client_name || "-"}</Typography>
                    <Typography sx={{ color: "text.disabled", fontSize: 12 }}>{project.process_name || "-"}</Typography>
                    {project.ba_name ? <Typography sx={{ color: "text.disabled", fontSize: 12 }}>BA: {project.ba_name}</Typography> : null}
                  </TableCell>
                  <TableCell sx={{ color: "text.secondary", verticalAlign: "top", whiteSpace: "nowrap" }}>{formatDate(project.created_at)}</TableCell>
                  <TableCell sx={{ color: "text.secondary", verticalAlign: "top", whiteSpace: "nowrap" }}>{formatDate(project.updated_at)}</TableCell>
                  <TableCell sx={{ verticalAlign: "top" }}>
                    <Chip size="small" label={project.status || "draft"} sx={{ bgcolor: project.status === "exported" ? "rgba(76,175,80,0.15)" : project.status === "generating" ? "rgba(33,150,243,0.15)" : "rgba(242,101,34,0.12)", color: "text.primary", border: "1px solid rgba(242,101,34,0.2)" }} />
                  </TableCell>
                  <TableCell sx={{ color: "text.secondary", verticalAlign: "top" }}>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                      <Chip size="small" label={`Videos: ${formatTokens(project.video_count)}`} />
                      <Chip size="small" label={`Captures: ${formatTokens(project.capture_count)}`} />
                      <Chip size="small" label={`Sections: ${formatTokens(project.section_count)}`} />
                      <Chip size="small" label={`Docs: ${formatTokens(project.document_count)}`} />
                    </Box>
                  </TableCell>
                  <TableCell sx={{ color: "text.secondary", verticalAlign: "top", minWidth: 320 }}>
                    <Typography sx={{ color: "text.primary", fontWeight: 600 }}>{formatFinalBrd(project.final_version)}</Typography>
                    <Typography sx={{ color: "text.disabled", fontSize: 12 }}>{project.final_version?.created_at ? `Saved ${formatDate(project.final_version.created_at)}` : "No full BRD snapshot yet"}</Typography>
                    {project.final_version?.summary ? <Typography sx={{ color: "text.secondary", fontSize: 12, mt: 0.5 }}>{project.final_version.summary}</Typography> : null}
                  </TableCell>
                  <TableCell sx={{ verticalAlign: "top" }}>
                    <Button variant="contained" size="small" color="error" startIcon={<DeleteForeverIcon sx={{ fontSize: 16 }} />} onClick={() => handlePurgeProject(project)}>Purge</Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination component="div" count={brdProjects.length} page={brdPage} onPageChange={(_, nextPage) => setBrdPage(nextPage)} rowsPerPage={brdRowsPerPage} onRowsPerPageChange={(e) => { setBrdRowsPerPage(parseInt(e.target.value, 10)); setBrdPage(0); }} rowsPerPageOptions={[8, 12, 16]} sx={{ color: "text.secondary", borderTop: "1px solid var(--ae-border)", flexShrink: 0 }} />
    </Paper>
  );

  const renderSddBrdTable = () => (
    <Paper elevation={0} sx={{ background: glassBg, backdropFilter: "blur(12px)", borderRadius: "24px", border: `1px solid ${glassBorder}`, boxShadow: isDark ? "0 12px 40px rgba(0,0,0,0.4)" : "0 10px 30px rgba(15,23,42,0.06)", display: "flex", flexDirection: "column", flex: 1, minHeight: 0, overflow: "hidden" }}>
      <Box sx={{ px: 2, py: 1.5, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, flexWrap: "wrap", borderBottom: "1px solid var(--ae-border)" }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" sx={{ color: "text.primary", fontWeight: 700 }}>BRD to SDD Admin</Typography>
          <Typography sx={{ color: "text.secondary", fontSize: 13 }}>Manage and purge BRD to SDD generation history records.</Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
          <TextField size="small" type="number" label="Purge days" value={purgeDays} onChange={e => setPurgeDays(e.target.value)} sx={{ width: 140 }} />
          <Button variant="contained" color="error" onClick={() => handleBulkPurgeSdd("brd")} disabled={purging} startIcon={<DeleteForeverIcon />}>Purge Old</Button>
          <Button variant="outlined" onClick={fetchSddBrdHistory} startIcon={<RefreshIcon />}>Refresh</Button>
        </Box>
      </Box>
      <TableContainer sx={{ flex: 1, overflowY: "auto" }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ bgcolor: headerBg, color: "text.primary", fontWeight: "bold" }}>Filename<HeaderSearchField placeholder="Search Filename" value={sddBrdColFilters.filename} onChange={(v) => setSddBrdColFilters(p => ({ ...p, filename: v }))} isDark={isDark} /></TableCell>
              <TableCell sx={{ bgcolor: headerBg, color: "text.primary", fontWeight: "bold" }}>User<HeaderSearchField placeholder="Search User" value={sddBrdColFilters.user} onChange={(v) => setSddBrdColFilters(p => ({ ...p, user: v }))} isDark={isDark} /></TableCell>
              <TableCell sx={{ bgcolor: headerBg, color: "text.primary", fontWeight: "bold" }}>Email<HeaderSearchField placeholder="Search Email" value={sddBrdColFilters.email} onChange={(v) => setSddBrdColFilters(p => ({ ...p, email: v }))} isDark={isDark} /></TableCell>
              <TableCell sx={{ bgcolor: headerBg, color: "text.primary", fontWeight: "bold" }}>Input Tokens</TableCell>
              <TableCell sx={{ bgcolor: headerBg, color: "text.primary", fontWeight: "bold" }}>Output Tokens</TableCell>
              <TableCell sx={{ bgcolor: headerBg, color: "text.primary", fontWeight: "bold" }}>Created</TableCell>
              <TableCell sx={{ bgcolor: headerBg, color: "text.primary", fontWeight: "bold" }}>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredSddBrdHistory.length === 0 ? (
              <TableRow><TableCell colSpan={7} align="center" sx={{ color: "text.secondary", py: 4 }}>No records found.</TableCell></TableRow>
            ) : (
              filteredSddBrdHistory.slice(sddBrdPage * sddBrdRowsPerPage, sddBrdPage * sddBrdRowsPerPage + sddBrdRowsPerPage).map(item => (
                <TableRow key={item.id} sx={{ "&:hover": { bgcolor: "var(--ae-surface)" } }}>
                  <TableCell sx={{ color: "text.primary" }}>{item.filename}</TableCell>
                  <TableCell sx={{ color: "text.secondary" }}>{item.username}</TableCell>
                  <TableCell sx={{ color: "text.secondary" }}>{item.email}</TableCell>
                  <TableCell sx={{ color: "text.secondary" }}>{formatTokens(item.input_tokens)}</TableCell>
                  <TableCell sx={{ color: "text.secondary" }}>{formatTokens(item.output_tokens)}</TableCell>
                  <TableCell sx={{ color: "text.secondary" }}>{formatDate(item.created_at)}</TableCell>
                  <TableCell><Button size="small" color="error" onClick={() => handlePurgeSddBrd(item.id)}><DeleteForeverIcon /></Button></TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination component="div" count={filteredSddBrdHistory.length} page={sddBrdPage} onPageChange={(_, p) => setSddBrdPage(p)} rowsPerPage={sddBrdRowsPerPage} onRowsPerPageChange={e => setSddBrdRowsPerPage(parseInt(e.target.value, 10))} rowsPerPageOptions={[8, 12, 16]} sx={{ borderTop: "1px solid var(--ae-border)" }} />
    </Paper>
  );

  const renderSddWfTable = () => (
    <Paper elevation={0} sx={{ background: glassBg, backdropFilter: "blur(12px)", borderRadius: "24px", border: `1px solid ${glassBorder}`, boxShadow: isDark ? "0 12px 40px rgba(0,0,0,0.4)" : "0 10px 30px rgba(15,23,42,0.06)", display: "flex", flexDirection: "column", flex: 1, minHeight: 0, overflow: "hidden" }}>
      <Box sx={{ px: 2, py: 1.5, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, flexWrap: "wrap", borderBottom: "1px solid var(--ae-border)" }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" sx={{ color: "text.primary", fontWeight: 700 }}>Workflow to SDD Admin</Typography>
          <Typography sx={{ color: "text.secondary", fontSize: 13 }}>Manage and purge Workflow to SDD generation history records.</Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
          <TextField size="small" type="number" label="Purge days" value={purgeDays} onChange={e => setPurgeDays(e.target.value)} sx={{ width: 140 }} />
          <Button variant="contained" color="error" onClick={() => handleBulkPurgeSdd("workflow")} disabled={purging} startIcon={<DeleteForeverIcon />}>Purge Old</Button>
          <Button variant="outlined" onClick={fetchSddWfHistory} startIcon={<RefreshIcon />}>Refresh</Button>
        </Box>
      </Box>
      <TableContainer sx={{ flex: 1, overflowY: "auto" }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ bgcolor: headerBg, color: "text.primary", fontWeight: "bold" }}>Project / File<HeaderSearchField placeholder="Search Project/File" value={sddWfColFilters.project} onChange={(v) => setSddWfColFilters(p => ({ ...p, project: v }))} isDark={isDark} /></TableCell>
              <TableCell sx={{ bgcolor: headerBg, color: "text.primary", fontWeight: "bold" }}>Client<HeaderSearchField placeholder="Search Client" value={sddWfColFilters.client} onChange={(v) => setSddWfColFilters(p => ({ ...p, client: v }))} isDark={isDark} /></TableCell>
              <TableCell sx={{ bgcolor: headerBg, color: "text.primary", fontWeight: "bold" }}>User<HeaderSearchField placeholder="Search User" value={sddWfColFilters.user} onChange={(v) => setSddWfColFilters(p => ({ ...p, user: v }))} isDark={isDark} /></TableCell>
              <TableCell sx={{ bgcolor: headerBg, color: "text.primary", fontWeight: "bold" }}>Status<HeaderSearchField placeholder="Search Status" value={sddWfColFilters.status} onChange={(v) => setSddWfColFilters(p => ({ ...p, status: v }))} isDark={isDark} /></TableCell>
              <TableCell sx={{ bgcolor: headerBg, color: "text.primary", fontWeight: "bold" }}>Created</TableCell>
              <TableCell sx={{ bgcolor: headerBg, color: "text.primary", fontWeight: "bold" }}>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredSddWfHistory.length === 0 ? (
              <TableRow><TableCell colSpan={8} align="center" sx={{ color: "text.secondary", py: 4 }}>No records found.</TableCell></TableRow>
            ) : (
              filteredSddWfHistory.slice(sddWfPage * sddWfRowsPerPage, sddWfPage * sddWfRowsPerPage + sddWfRowsPerPage).map(item => (
                <TableRow key={item.id} sx={{ "&:hover": { bgcolor: "var(--ae-surface)" } }}>
                  <TableCell sx={{ color: "text.primary" }}>{item.project_name || item.file_name}</TableCell>
                  <TableCell sx={{ color: "text.secondary" }}>{item.client_name || "-"}</TableCell>
                  <TableCell sx={{ color: "text.secondary" }}>{item.user_name || "-"}</TableCell>
                  <TableCell sx={{ color: "text.secondary" }}>{item.status}</TableCell>
                  <TableCell sx={{ color: "text.secondary" }}>{formatDate(item.created_at)}</TableCell>
                  <TableCell><Button size="small" color="error" onClick={() => handlePurgeSddWf(item.id)}><DeleteForeverIcon /></Button></TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination component="div" count={filteredSddWfHistory.length} page={sddWfPage} onPageChange={(_, p) => setSddWfPage(p)} rowsPerPage={sddWfRowsPerPage} onRowsPerPageChange={e => setSddWfRowsPerPage(parseInt(e.target.value, 10))} rowsPerPageOptions={[8, 12, 16]} sx={{ borderTop: "1px solid var(--ae-border)" }} />
    </Paper>
  );

  const renderTestCaseTable = () => (
    <Paper elevation={0} sx={{ background: glassBg, backdropFilter: "blur(12px)", borderRadius: "24px", border: `1px solid ${glassBorder}`, boxShadow: isDark ? "0 12px 40px rgba(0,0,0,0.4)" : "0 10px 30px rgba(15,23,42,0.06)", display: "flex", flexDirection: "column", flex: 1, minHeight: 0, overflow: "hidden" }}>
      <Box sx={{ px: 2, py: 1.5, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, flexWrap: "wrap", borderBottom: "1px solid var(--ae-border)" }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" sx={{ color: "text.primary", fontWeight: 700 }}>Test Case Management</Typography>
          <Typography sx={{ color: "text.secondary", fontSize: 13 }}>List, search, download and purge generated test case Excel files.</Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
          <TextField size="small" type="number" label="Purge days" value={purgeDays} onChange={e => setPurgeDays(e.target.value)} sx={{ width: 140 }} />
          <Button variant="contained" color="error" onClick={handleBulkPurgeTestCases} disabled={purging} startIcon={<DeleteForeverIcon />}>Purge Old</Button>
          <Button variant="outlined" onClick={fetchTestCaseHistory} startIcon={<RefreshIcon />}>Refresh</Button>
        </Box>
      </Box>
      <TableContainer sx={{ flex: 1, overflowY: "auto" }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ bgcolor: headerBg, color: "text.primary", fontWeight: "bold" }}>Project Name<HeaderSearchField placeholder="Search Project" value={testCaseColFilters.project} onChange={(v) => setTestCaseColFilters(p => ({ ...p, project: v }))} isDark={isDark} /></TableCell>
              <TableCell sx={{ bgcolor: headerBg, color: "text.primary", fontWeight: "bold" }}>Creator Email<HeaderSearchField placeholder="Search Email" value={testCaseColFilters.email} onChange={(v) => setTestCaseColFilters(p => ({ ...p, email: v }))} isDark={isDark} /></TableCell>
              <TableCell sx={{ bgcolor: headerBg, color: "text.primary", fontWeight: "bold" }}>User ID<HeaderSearchField placeholder="Search ID" value={testCaseColFilters.user} onChange={(v) => setTestCaseColFilters(p => ({ ...p, user: v }))} isDark={isDark} /></TableCell>
              <TableCell sx={{ bgcolor: headerBg, color: "text.primary", fontWeight: "bold" }}>Created At</TableCell>
              <TableCell sx={{ bgcolor: headerBg, color: "text.primary", fontWeight: "bold" }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredTestCaseHistory.length === 0 ? (
              <TableRow><TableCell colSpan={5} align="center" sx={{ color: "text.secondary", py: 4 }}>No test case files found.</TableCell></TableRow>
            ) : (
              filteredTestCaseHistory.slice(testCasePage * testCaseRowsPerPage, testCasePage * testCaseRowsPerPage + testCaseRowsPerPage).map(item => (
                <TableRow key={item.filename} sx={{ "&:hover": { bgcolor: "var(--ae-surface)" } }}>
                  <TableCell sx={{ color: "text.primary" }}>{item.project_name}</TableCell>
                  <TableCell sx={{ color: "text.secondary" }}>{item.user_email}</TableCell>
                  <TableCell sx={{ color: "text.secondary" }}>{item.user_id}</TableCell>
                  <TableCell sx={{ color: "text.secondary" }}>{formatDate(item.created_at)}</TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Button size="small" variant="contained" onClick={() => handleDownloadTestCase(item.filename)}>Download</Button>
                      <Button size="small" variant="outlined" color="error" onClick={() => handlePurgeTestCase(item.filename)}>Delete</Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination component="div" count={filteredTestCaseHistory.length} page={testCasePage} onPageChange={(_, p) => setTestCasePage(p)} rowsPerPage={testCaseRowsPerPage} onRowsPerPageChange={e => setTestCaseRowsPerPage(parseInt(e.target.value, 10))} rowsPerPageOptions={[8, 12, 16]} sx={{ borderTop: "1px solid var(--ae-border)" }} />
    </Paper>
  );

  const renderTrainingTable = () => (
    <Paper elevation={0} sx={{ background: glassBg, backdropFilter: "blur(12px)", borderRadius: "24px", border: `1px solid ${glassBorder}`, boxShadow: isDark ? "0 12px 40px rgba(0,0,0,0.4)" : "0 10px 30px rgba(15,23,42,0.06)", display: "flex", flexDirection: "column", flex: 1, minHeight: 0, overflow: "hidden" }}>
      <Box sx={{ px: 2, py: 1.5, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, flexWrap: "wrap", borderBottom: "1px solid var(--ae-border)" }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" sx={{ color: "text.primary", fontWeight: 700 }}>Training Evaluation History</Typography>
          <Typography sx={{ color: "text.secondary", fontSize: 13 }}>Review and manage trainee evaluation records and token usage.</Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
          <Button variant="outlined" onClick={fetchTrainingHistory} startIcon={<RefreshIcon />}>Refresh</Button>
        </Box>
      </Box>
      <TableContainer sx={{ flex: 1, overflowY: "auto" }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ bgcolor: headerBg, color: "text.primary", fontWeight: "bold" }}>Trainee Name<HeaderSearchField placeholder="Search Trainee" value={trainingColFilters.trainee} onChange={(v) => setTrainingColFilters(p => ({ ...p, trainee: v }))} isDark={isDark} /></TableCell>
              <TableCell sx={{ bgcolor: headerBg, color: "text.primary", fontWeight: "bold" }}>Module<HeaderSearchField placeholder="Search Module" value={trainingColFilters.module} onChange={(v) => setTrainingColFilters(p => ({ ...p, module: v }))} isDark={isDark} /></TableCell>
              <TableCell sx={{ bgcolor: headerBg, color: "text.primary", fontWeight: "bold" }}>Admin (User)<HeaderSearchField placeholder="Search Admin" value={trainingColFilters.email} onChange={(v) => setTrainingColFilters(p => ({ ...p, email: v }))} isDark={isDark} /></TableCell>
              <TableCell sx={{ bgcolor: headerBg, color: "text.primary", fontWeight: "bold" }}>Score</TableCell>
              <TableCell sx={{ bgcolor: headerBg, color: "text.primary", fontWeight: "bold" }}>Tokens (I/O)</TableCell>
              <TableCell sx={{ bgcolor: headerBg, color: "text.primary", fontWeight: "bold" }}>Checked At</TableCell>
              <TableCell sx={{ bgcolor: headerBg, color: "text.primary", fontWeight: "bold" }}>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredTrainingHistory.length === 0 ? (
              <TableRow><TableCell colSpan={7} align="center" sx={{ color: "text.secondary", py: 4 }}>No records found.</TableCell></TableRow>
            ) : (
              filteredTrainingHistory.slice(trainingPage * trainingRowsPerPage, trainingPage * trainingRowsPerPage + trainingRowsPerPage).map(item => (
                <TableRow key={item.id} sx={{ "&:hover": { bgcolor: "var(--ae-surface)" } }}>
                  <TableCell sx={{ color: "text.primary" }}>{item.trainee_name}</TableCell>
                  <TableCell sx={{ color: "text.secondary" }}>{item.trainee_module}</TableCell>
                  <TableCell sx={{ color: "text.secondary" }}>{item.email}</TableCell>
                  <TableCell sx={{ color: "#F26522", fontWeight: "bold" }}>{item.total_awarded} / {item.total_max}</TableCell>
                  <TableCell sx={{ color: "text.secondary", fontSize: 12 }}>{formatTokens(item.input_tokens)} / {formatTokens(item.output_tokens)}</TableCell>
                  <TableCell sx={{ color: "text.secondary" }}>{formatDate(item.checked_at)}</TableCell>
                  <TableCell><Button size="small" color="error" onClick={() => handlePurgeTraining(item.id)}><DeleteForeverIcon /></Button></TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination component="div" count={filteredTrainingHistory.length} page={trainingPage} onPageChange={(_, p) => setTrainingPage(p)} rowsPerPage={trainingRowsPerPage} onRowsPerPageChange={e => setTrainingRowsPerPage(parseInt(e.target.value, 10))} rowsPerPageOptions={[8, 12, 16]} sx={{ borderTop: "1px solid var(--ae-border)" }} />
    </Paper>
  );

  const TYPE_COLORS = { email: "#3b82f6", transcript: "#8b5cf6", onedrive_file: "#10b981" };
  const TYPE_LABELS = { email: "Email", transcript: "Transcript", onedrive_file: "OneDrive File" };

  const filteredCsmItems = csmItems.filter(item => {
    const matchType = !csmTypeFilter || (item.item_type || "").toLowerCase().includes(csmTypeFilter.toLowerCase());
    const matchId = !csmIdFilter ||
      (item.item_name || "").toLowerCase().includes(csmIdFilter.toLowerCase()) ||
      (item.item_id || "").toLowerCase().includes(csmIdFilter.toLowerCase());
    return matchType && matchId;
  });

  const handleSaveScheduler = async () => {
    if (!csmSchedulerDraft) return;
    setCsmSchedulerSaving(true); setCsmError(""); setCsmMessage("");
    try {
      const token = localStorage.getItem("edge_token");
      const payload = {
        interval_minutes: Math.max(1, parseInt(csmSchedulerDraft.interval_minutes) || 30),
        scheduler_enabled: csmSchedulerDraft.scheduler_enabled,
        fetch_since: csmSchedulerDraft.fetch_since || null,
        fetch_until: csmSchedulerDraft.fetch_until || null,
      };
      const res = await axios.put("/api/admin/csm/scheduler/settings", payload, { headers: { Authorization: `Bearer ${token}` } });
      setCsmScheduler(res.data);
      setCsmSchedulerDraft({ interval_minutes: res.data.interval_minutes, scheduler_enabled: res.data.scheduler_enabled, fetch_since: res.data.fetch_since || "", fetch_until: res.data.fetch_until || "" });
      setCsmMessage("Scheduler settings saved. Changes take effect on the next scheduler cycle.");
    } catch (e) { setCsmError("Failed to save scheduler settings: " + (e.response?.data?.detail || e.message)); }
    finally { setCsmSchedulerSaving(false); }
  };

  const handleSearchTranscripts = async () => {
    if (!csmSearchDate) return;
    setCsmSearchLoading(true);
    setCsmSearchError("");
    try {
      const token = localStorage.getItem("edge_token");
      const res = await axios.get(`/api/admin/csm/transcripts/search?date=${csmSearchDate}`, { headers: { Authorization: `Bearer ${token}` } });
      setCsmSearchResults(res.data);
    } catch (e) {
      setCsmSearchError(e.response?.data?.detail || e.message || "Failed to search transcripts.");
    } finally {
      setCsmSearchLoading(false);
    }
  };

  const handleToggleSkip = async (item, skipValue) => {
    setCsmTogglingItemId(item.item_id);
    try {
      const token = localStorage.getItem("edge_token");
      await axios.post(
        "/api/admin/csm/transcripts/skip",
        {
          item_id: item.item_id,
          item_type: item.item_type,
          item_name: item.item_name,
          source: item.source || (item.item_type === "onedrive_file" ? "OneDrive" : "Calendar"),
          received_at: item.received_at,
          skip: skipValue,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCsmSearchResults(prev =>
        prev.map(i => (i.item_id === item.item_id ? { ...i, is_skipped: skipValue } : i))
      );
      fetchCsmData();
    } catch (e) {
      setCsmError("Failed to update skip status: " + (e.response?.data?.detail || e.message));
    } finally {
      setCsmTogglingItemId("");
    }
  };

  const renderCsmTab = () => (
    <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, overflow: "hidden" }}>
      {csmError && <Alert severity="error" sx={{ borderRadius: "12px", mb: 1, flexShrink: 0 }}>{csmError}</Alert>}
      {csmMessage && <Alert severity="success" sx={{ borderRadius: "12px", mb: 1, flexShrink: 0 }}>{csmMessage}</Alert>}

      {/* Inner tab bar */}
      <Box sx={{ flexShrink: 0, mb: 1.5 }}>
        <Tabs
          value={csmInnerTab}
          onChange={(_, v) => setCsmInnerTab(v)}
          textColor="inherit"
          TabIndicatorProps={{ style: { background: "#F26522", height: 2, borderRadius: "2px" } }}
          sx={{ borderBottom: "1px solid var(--ae-border)", minHeight: 36, "& .MuiTab-root": { color: "text.secondary", fontWeight: 700, textTransform: "none", fontSize: 12, minHeight: 36, py: 0.5 }, "& .Mui-selected": { color: "#F26522 !important" } }}
        >
          <Tab label="Overview & Scheduler" />
          <Tab label={`Processed Items${csmItems.length ? ` (${csmItems.length})` : ""}`} />
        </Tabs>
      </Box>

      {/* TAB 0 — Overview & Scheduler */}
      {csmInnerTab === 0 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, flex: 1, minHeight: 0, overflowY: "auto", pr: 0.5 }}>
          {/* Stats Row */}
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 1.5 }}>
            {[
              { label: "Accounts", value: csmStatus?.accounts_count ?? "—", color: "#6366f1" },
              { label: "Total Signals", value: csmStatus?.signals_count ?? "—", color: "#F26522" },
              { label: "Open Signals", value: csmStatus?.open_signals_count ?? "—", color: "#f59e0b" },
              { label: "Critical", value: csmStatus?.critical_signals_count ?? "—", color: "#ef4444" },
            ].map(card => (
              <Paper key={card.label} elevation={0} sx={{ p: 2, borderRadius: "16px", background: glassBg, border: `1px solid ${glassBorder}`, borderTop: `3px solid ${card.color}` }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "text.secondary", letterSpacing: "0.08em" }}>{card.label}</Typography>
                <Typography sx={{ fontSize: 28, fontWeight: 300, color: "text.primary", fontFamily: "monospace", mt: 0.5 }}>{card.value}</Typography>
              </Paper>
            ))}
          </Box>

          {/* Scheduler Settings Card */}
          <Paper elevation={0} sx={{ p: 2, borderRadius: "16px", background: glassBg, border: `1px solid ${glassBorder}` }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5, flexWrap: "wrap", gap: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary" }}>Live Ingestion Scheduler</Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, px: 1, py: 0.25, borderRadius: "999px", background: csmScheduler.scheduler_enabled ? "rgba(16,185,129,0.12)" : "rgba(148,163,184,0.12)", border: `1px solid ${csmScheduler.scheduler_enabled ? "rgba(16,185,129,0.3)" : "rgba(148,163,184,0.3)"}` }}>
                  <Box sx={{ width: 7, height: 7, borderRadius: "50%", background: csmScheduler.scheduler_enabled ? "#10b981" : "#94a3b8", animation: csmScheduler.scheduler_enabled ? "pulse 2s infinite" : "none", "@keyframes pulse": { "0%,100%": { opacity: 1 }, "50%": { opacity: 0.3 } } }} />
                  <Typography sx={{ fontSize: 11, fontWeight: 700, color: csmScheduler.scheduler_enabled ? "#10b981" : "#94a3b8" }}>
                    {csmScheduler.scheduler_enabled ? "Running" : "Stopped"}
                  </Typography>
                </Box>
              </Box>
              {csmScheduler.updated_at && (
                <Typography sx={{ fontSize: 11, color: "text.disabled" }}>Last saved: {formatDate(csmScheduler.updated_at)}</Typography>
              )}
            </Box>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "flex-end" }}>
              <Box>
                <Typography sx={{ fontSize: 11, fontWeight: 600, color: "text.secondary", mb: 0.5 }}>Fetch Interval (minutes)</Typography>
                <TextField size="small" type="number" value={csmSchedulerDraft?.interval_minutes ?? 30}
                  onChange={e => setCsmSchedulerDraft(p => ({ ...p, interval_minutes: e.target.value }))}
                  inputProps={{ min: 1, max: 1440 }}
                  sx={{ width: 140, "& .MuiInputBase-root": { borderRadius: "10px", fontSize: 13 } }} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: 11, fontWeight: 600, color: "text.secondary", mb: 0.5 }}>Fetch Since (from date)</Typography>
                <TextField size="small" type="date" value={csmSchedulerDraft?.fetch_since || ""}
                  onChange={e => setCsmSchedulerDraft(p => ({ ...p, fetch_since: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  sx={{ width: 165, "& .MuiInputBase-root": { borderRadius: "10px", fontSize: 13 } }} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: 11, fontWeight: 600, color: "text.secondary", mb: 0.5 }}>Fetch Until (end date)</Typography>
                <TextField size="small" type="date" value={csmSchedulerDraft?.fetch_until || ""}
                  onChange={e => setCsmSchedulerDraft(p => ({ ...p, fetch_until: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  sx={{ width: 165, "& .MuiInputBase-root": { borderRadius: "10px", fontSize: 13 } }} />
              </Box>
              {(csmSchedulerDraft?.fetch_since || csmSchedulerDraft?.fetch_until) && (
                <Button size="small" variant="text"
                  onClick={() => setCsmSchedulerDraft(p => ({ ...p, fetch_since: "", fetch_until: "" }))}
                  sx={{ color: "text.disabled", fontSize: 11, textTransform: "none", alignSelf: "flex-end", mb: 0.25 }}>
                  Clear settings
                </Button>
              )}
              <Button size="small" variant="outlined"
                onClick={() => setCsmSchedulerDraft(p => ({ ...p, scheduler_enabled: !p.scheduler_enabled }))}
                sx={{ borderRadius: "10px", textTransform: "none", fontSize: 12, alignSelf: "flex-end", borderColor: csmSchedulerDraft?.scheduler_enabled ? "rgba(220,38,38,0.4)" : "rgba(16,185,129,0.4)", color: csmSchedulerDraft?.scheduler_enabled ? "#dc2626" : "#10b981", "&:hover": { background: csmSchedulerDraft?.scheduler_enabled ? "rgba(220,38,38,0.06)" : "rgba(16,185,129,0.06)" } }}>
                {csmSchedulerDraft?.scheduler_enabled ? "Stop Scheduler" : "Start Scheduler"}
              </Button>
              <Button size="small" variant="contained" onClick={handleSaveScheduler} disabled={csmSchedulerSaving}
                startIcon={csmSchedulerSaving ? <CircularProgress size={13} sx={{ color: "#fff" }} /> : <SaveIcon sx={{ fontSize: 14 }} />}
                sx={{ borderRadius: "10px", textTransform: "none", fontSize: 12, background: "#F26522", "&:hover": { background: "#d4551a" }, alignSelf: "flex-end" }}>
                Save Settings
              </Button>
            </Box>
            <Typography sx={{ fontSize: 11, color: "text.disabled", mt: 1 }}>
              Changes take effect on the next scheduler cycle. "Fetch Since/Until" filters which emails &amp; transcripts are ingested by date.
            </Typography>
          </Paper>

          {/* Day-Wise Transcript Search and Exclude Selection Card */}
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: "16px", background: glassBg, border: `1px solid ${glassBorder}` }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
              <Box>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary" }}>Search &amp; Skip Specific Transcripts</Typography>
                <Typography sx={{ fontSize: 11, color: "text.secondary", mt: 0.5 }}>
                  Look up meeting transcripts and OneDrive files by date to selectively skip processing for specific items.
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2 }}>
              <Box>
                <Typography sx={{ fontSize: 11, fontWeight: 600, color: "text.secondary", mb: 0.5 }}>Search Date</Typography>
                <TextField size="small" type="date" value={csmSearchDate}
                  onChange={e => setCsmSearchDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ width: 180, "& .MuiInputBase-root": { borderRadius: "10px", fontSize: 13 } }} />
              </Box>
              <Button size="small" variant="contained" onClick={handleSearchTranscripts} disabled={csmSearchLoading}
                startIcon={csmSearchLoading ? <CircularProgress size={13} sx={{ color: "#fff" }} /> : <SearchIcon sx={{ fontSize: 14 }} />}
                sx={{ borderRadius: "10px", textTransform: "none", fontSize: 12, background: "#F26522", "&:hover": { background: "#d4551a" }, height: 36, mt: 2.2 }}>
                Search Transcripts
              </Button>
            </Box>

            {csmSearchError && (
              <Alert severity="error" sx={{ borderRadius: "10px", mb: 2, fontSize: 12 }}>
                {csmSearchError}
              </Alert>
            )}

            {csmSearchLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress size={24} sx={{ color: "#F26522" }} />
              </Box>
            ) : csmSearchResults.length > 0 ? (
              <TableContainer sx={{ border: "1px solid rgba(226,232,240,0.1)", borderRadius: "12px", background: "rgba(255,255,255,0.01)" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ background: "rgba(242,101,34,0.04)" }}>
                      <TableCell sx={{ fontWeight: 600, fontSize: 11, color: "text.secondary", py: 1 }}>Name</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: 11, color: "text.secondary", py: 1 }}>Source</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: 11, color: "text.secondary", py: 1 }}>Time</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600, fontSize: 11, color: "text.secondary", py: 1, width: 120 }}>Skip Ingestion</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {csmSearchResults.map(item => (
                      <TableRow key={item.item_id} hover sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                        <TableCell sx={{ fontSize: 12, fontWeight: 500, color: "text.primary", py: 1 }}>
                          {item.item_name}
                        </TableCell>
                        <TableCell sx={{ py: 1 }}>
                          <Chip label={item.source} size="small" sx={{ fontSize: 10, fontWeight: 600, height: 20, background: item.source === "OneDrive" ? "rgba(37,99,235,0.1)" : "rgba(16,185,129,0.1)", color: item.source === "OneDrive" ? "#2563eb" : "#10b981", border: "1px solid currentColor" }} />
                        </TableCell>
                        <TableCell sx={{ fontSize: 11, color: "text.secondary", py: 1 }}>
                          {item.received_at ? new Date(item.received_at).toLocaleString() : "N/A"}
                        </TableCell>
                        <TableCell align="center" sx={{ py: 1 }}>
                          {csmTogglingItemId === item.item_id ? (
                            <CircularProgress size={16} sx={{ color: "#F26522" }} />
                          ) : (
                            <Switch size="small" checked={item.is_skipped}
                              onChange={e => handleToggleSkip(item, e.target.checked)}
                              color="warning" />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ py: 3, textAlign: "center", border: "1px dashed rgba(226,232,240,0.15)", borderRadius: "12px", background: "rgba(255,255,255,0.01)" }}>
                <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                  {csmSearchDate ? "No transcripts or files found for this date." : "Select a date to search for transcripts and configure individual skip/exclusion rules."}
                </Typography>
              </Box>
            )}
          </Paper>

          {/* Portfolio Graph + Actions */}
          <Paper elevation={0} sx={{ p: 2, borderRadius: "16px", background: glassBg, border: `1px solid ${glassBorder}`, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary" }}>Portfolio Graph</Typography>
              <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                Last updated: {csmStatus?.last_updated ? formatDate(csmStatus.last_updated) : "Never"}
                {" · "}Processed items: {csmStatus?.processed_items_total ?? 0}
                {csmStatus?.processed_by_type && Object.keys(csmStatus.processed_by_type).length > 0 && (
                  <span> ({Object.entries(csmStatus.processed_by_type).map(([k, v]) => `${v} ${TYPE_LABELS[k] || k}`).join(", ")})</span>
                )}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Button variant="outlined" size="small" onClick={fetchCsmData} startIcon={<RefreshIcon sx={{ fontSize: 15 }} />}
                sx={{ borderRadius: "999px", textTransform: "none", fontSize: 12, borderColor: "rgba(242,101,34,0.3)", color: "#F26522", "&:hover": { borderColor: "#F26522", background: "rgba(242,101,34,0.06)" } }}>
                Refresh
              </Button>
              <Button variant="contained" size="small" disabled={csmFullIngesting || csmLiveIngesting}
                onClick={async () => {
                  setCsmFullIngesting(true); setCsmMessage(""); setCsmError("");
                  try {
                    const token = localStorage.getItem("edge_token");
                    const res = await axios.post("/api/admin/csm/ingest", {}, { headers: { Authorization: `Bearer ${token}` } });
                    setCsmMessage(`Full ingestion complete. Added: ${res.data.total_added_signals}, Updated: ${res.data.total_updated_signals}`);
                    fetchCsmData();
                  } catch (e) { setCsmError("Full ingestion failed: " + (e.response?.data?.detail || e.message)); }
                  finally { setCsmFullIngesting(false); }
                }}
                sx={{ borderRadius: "999px", textTransform: "none", fontSize: 12, background: "#F26522", "&:hover": { background: "#d4551a" } }}>
                {csmFullIngesting ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : "Run Full Ingestion"}
              </Button>
              <Button variant="outlined" size="small" disabled={csmFullIngesting || csmLiveIngesting}
                onClick={async () => {
                  setCsmLiveIngesting(true); setCsmMessage(""); setCsmError("");
                  try {
                    const token = localStorage.getItem("edge_token");
                    const res = await axios.post("/api/admin/csm/ingest/live", {}, { headers: { Authorization: `Bearer ${token}` } });
                    setCsmMessage(`Live ingestion complete. Added: ${res.data.total_added_signals}, Updated: ${res.data.total_updated_signals}, Items: ${res.data.processed_items?.length ?? 0}`);
                    fetchCsmData();
                  } catch (e) { setCsmError("Live ingestion failed: " + (e.response?.data?.detail || e.message)); }
                  finally { setCsmLiveIngesting(false); }
                }}
                sx={{ borderRadius: "999px", textTransform: "none", fontSize: 12, borderColor: "rgba(16,185,129,0.4)", color: "#10b981", "&:hover": { borderColor: "#10b981", background: "rgba(16,185,129,0.06)" } }}>
                {csmLiveIngesting ? <CircularProgress size={14} sx={{ color: "#10b981" }} /> : "Run Live Ingestion"}
              </Button>
              <Button variant="outlined" size="small"
                onClick={async () => {
                  if (!window.confirm("This will allow all emails/transcripts to be re-ingested. Continue?")) return;
                  try {
                    const token = localStorage.getItem("edge_token");
                    await axios.delete("/api/admin/csm/processed-items", { headers: { Authorization: `Bearer ${token}` } });
                    setCsmMessage("Processed item history cleared. Next ingestion will re-process all items.");
                    fetchCsmData();
                  } catch (e) { setCsmError("Failed to clear history."); }
                }}
                sx={{ borderRadius: "999px", textTransform: "none", fontSize: 12, borderColor: "rgba(220,38,38,0.3)", color: "#dc2626", "&:hover": { borderColor: "#dc2626", background: "rgba(220,38,38,0.06)" } }}>
                Clear History
              </Button>
            </Box>
          </Paper>
        </Box>
      )}

      {/* TAB 1 — Processed Items table, full height */}
      {csmInnerTab === 1 && (
        <Paper elevation={0} sx={{ background: glassBg, backdropFilter: "blur(12px)", borderRadius: "16px", border: `1px solid ${glassBorder}`, display: "flex", flexDirection: "column", flex: 1, minHeight: 0, overflow: "hidden" }}>
          <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid var(--ae-border)", display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", flexShrink: 0 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ color: "text.primary", fontWeight: 700, fontSize: 15 }}>Processed Items</Typography>
              <Typography sx={{ color: "text.secondary", fontSize: 12 }}>Emails, transcripts and OneDrive files ingested into the CSM portfolio graph.</Typography>
            </Box>
            <TextField size="small" placeholder="Filter by type…" value={csmTypeFilter} onChange={e => { setCsmTypeFilter(e.target.value); setCsmPage(0); }}
              sx={{ width: 160, "& .MuiOutlinedInput-root": { borderRadius: "999px", fontSize: 12 } }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 15, color: "text.secondary" }} /></InputAdornment> }} />
            <TextField size="small" placeholder="Filter by name / ID…" value={csmIdFilter} onChange={e => { setCsmIdFilter(e.target.value); setCsmPage(0); }}
              sx={{ width: 200, "& .MuiOutlinedInput-root": { borderRadius: "999px", fontSize: 12 } }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 15, color: "text.secondary" }} /></InputAdornment> }} />
          </Box>
          <TableContainer sx={{ flex: 1, overflowY: "auto" }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ bgcolor: headerBg, fontWeight: 700, fontSize: 12, width: 120 }}>Type</TableCell>
                  <TableCell sx={{ bgcolor: headerBg, fontWeight: 700, fontSize: 12 }}>Name / Subject</TableCell>
                  <TableCell sx={{ bgcolor: headerBg, fontWeight: 700, fontSize: 12, width: 180 }}>Sender</TableCell>
                  <TableCell sx={{ bgcolor: headerBg, fontWeight: 700, fontSize: 12, width: 170 }}>Received At</TableCell>
                  <TableCell sx={{ bgcolor: headerBg, fontWeight: 700, fontSize: 12, width: 170 }}>Processed At</TableCell>
                  <TableCell align="center" sx={{ bgcolor: headerBg, fontWeight: 700, fontSize: 12, width: 130 }}>Skip Ingestion</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {csmLoading ? (
                  <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4 }}><CircularProgress size={20} sx={{ color: "#F26522" }} /></TableCell></TableRow>
                ) : filteredCsmItems.length === 0 ? (
                  <TableRow><TableCell colSpan={6} align="center" sx={{ color: "text.secondary", py: 4, fontSize: 13 }}>No processed items yet. Run an ingestion to populate this table.</TableCell></TableRow>
                ) : (
                  filteredCsmItems.slice(csmPage * csmRowsPerPage, csmPage * csmRowsPerPage + csmRowsPerPage).map(item => (
                    <TableRow key={item.id} sx={{ "&:hover": { bgcolor: "var(--ae-surface)" } }}>
                      <TableCell>
                        <Chip label={TYPE_LABELS[item.item_type] || item.item_type} size="small"
                          sx={{ bgcolor: `${TYPE_COLORS[item.item_type] || "#6366f1"}18`, color: TYPE_COLORS[item.item_type] || "#6366f1", fontWeight: 700, fontSize: 11, height: 22 }} />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 500 }}>
                        <Tooltip title={item.item_id} placement="top" arrow>
                          <Box>
                            <span style={{ fontSize: 12, color: "var(--ae-text-primary)", display: "block" }}>{item.item_name || "(no name)"}</span>
                            <span style={{ fontSize: 10, color: "var(--ae-text-secondary)", fontFamily: "monospace" }}>
                              {item.item_id.length > 40 ? item.item_id.slice(0, 40) + "…" : item.item_id}
                            </span>
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ color: "text.secondary", fontSize: 12 }}>{item.sender || "—"}</TableCell>
                      <TableCell sx={{ color: "text.secondary", fontSize: 12 }}>{item.received_at ? formatDate(item.received_at) : "—"}</TableCell>
                      <TableCell sx={{ color: "text.secondary", fontSize: 12 }}>{formatDate(item.processed_at)}</TableCell>
                      <TableCell align="center">
                        {csmTogglingItemId === item.item_id ? (
                          <CircularProgress size={16} sx={{ color: "#F26522" }} />
                        ) : (
                          <Switch size="small" checked={!!item.is_skipped}
                            onChange={e => handleToggleSkip(item, e.target.checked)}
                            color="warning" />
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination component="div" count={filteredCsmItems.length} page={csmPage} onPageChange={(_, p) => setCsmPage(p)}
            rowsPerPage={csmRowsPerPage} onRowsPerPageChange={e => setCsmRowsPerPage(parseInt(e.target.value, 10))}
            rowsPerPageOptions={[10, 20, 50]} sx={{ borderTop: "1px solid var(--ae-border)", fontSize: 12, flexShrink: 0 }} />
        </Paper>
      )}
    </Box>
  );

  const MAILBOX_LABELS = { sales_marketing: "Sales & Marketing", delivery_ba: "Delivery / BA" };
  const MAILBOX_COLORS = { sales_marketing: "#3b82f6", delivery_ba: "#8b5cf6" };

  const filteredCsmIndiaItems = csmIndiaItems.filter(item => {
    const matchMailbox = !csmIndiaMailboxFilter || (item.mailbox_team || "").toLowerCase().includes(csmIndiaMailboxFilter.toLowerCase());
    const matchId = !csmIndiaIdFilter ||
      (item.item_name || "").toLowerCase().includes(csmIndiaIdFilter.toLowerCase()) ||
      (item.item_id || "").toLowerCase().includes(csmIndiaIdFilter.toLowerCase()) ||
      (item.sender || "").toLowerCase().includes(csmIndiaIdFilter.toLowerCase());
    return matchMailbox && matchId;
  });

  const handleSaveIndiaScheduler = async () => {
    if (!csmIndiaSchedulerDraft) return;
    setCsmIndiaSchedulerSaving(true); setCsmIndiaError(""); setCsmIndiaMessage("");
    try {
      const token = localStorage.getItem("edge_token");
      const payload = {
        interval_minutes: Math.max(1, parseInt(csmIndiaSchedulerDraft.interval_minutes) || 30),
        scheduler_enabled: csmIndiaSchedulerDraft.scheduler_enabled,
        fetch_since: csmIndiaSchedulerDraft.fetch_since || null,
        fetch_until: csmIndiaSchedulerDraft.fetch_until || null,
      };
      const res = await axios.put("/api/admin/csm/india/scheduler/settings", payload, { headers: { Authorization: `Bearer ${token}` } });
      setCsmIndiaScheduler(res.data);
      setCsmIndiaSchedulerDraft({ interval_minutes: res.data.interval_minutes, scheduler_enabled: res.data.scheduler_enabled, fetch_since: res.data.fetch_since || "", fetch_until: res.data.fetch_until || "" });
      setCsmIndiaMessage("Scheduler settings saved. Changes take effect on the next scheduler cycle.");
    } catch (e) { setCsmIndiaError("Failed to save scheduler settings: " + (e.response?.data?.detail || e.message)); }
    finally { setCsmIndiaSchedulerSaving(false); }
  };

  const renderCsmIndiaTab = () => (
    <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, overflow: "hidden" }}>
      {csmIndiaError && <Alert severity="error" sx={{ borderRadius: "12px", mb: 1, flexShrink: 0 }}>{csmIndiaError}</Alert>}
      {csmIndiaMessage && <Alert severity="success" sx={{ borderRadius: "12px", mb: 1, flexShrink: 0 }}>{csmIndiaMessage}</Alert>}

      {/* Inner tab bar */}
      <Box sx={{ flexShrink: 0, mb: 1.5 }}>
        <Tabs
          value={csmIndiaInnerTab}
          onChange={(_, v) => setCsmIndiaInnerTab(v)}
          textColor="inherit"
          TabIndicatorProps={{ style: { background: "#F26522", height: 2, borderRadius: "2px" } }}
          sx={{ borderBottom: "1px solid var(--ae-border)", minHeight: 36, "& .MuiTab-root": { color: "text.secondary", fontWeight: 700, textTransform: "none", fontSize: 12, minHeight: 36, py: 0.5 }, "& .Mui-selected": { color: "#F26522 !important" } }}
        >
          <Tab label="Overview & Scheduler" />
          <Tab label={`Processed Items${csmIndiaItems.length ? ` (${csmIndiaItems.length})` : ""}`} />
        </Tabs>
      </Box>

      {/* TAB 0 — Overview & Scheduler */}
      {csmIndiaInnerTab === 0 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, flex: 1, minHeight: 0, overflowY: "auto", pr: 0.5 }}>
          {/* Stats Row */}
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 1.5 }}>
            {[
              { label: "Accounts", value: csmIndiaStatus?.accounts_count ?? "—", color: "#6366f1" },
              { label: "Adoption Signals", value: csmIndiaStatus?.adoption_signals_count ?? "—", color: "#10b981" },
              { label: "Pain Signals", value: csmIndiaStatus?.pain_signals_count ?? "—", color: "#f59e0b" },
              { label: "Critical", value: csmIndiaStatus?.critical_signals_count ?? "—", color: "#ef4444" },
            ].map(card => (
              <Paper key={card.label} elevation={0} sx={{ p: 2, borderRadius: "16px", background: glassBg, border: `1px solid ${glassBorder}`, borderTop: `3px solid ${card.color}` }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "text.secondary", letterSpacing: "0.08em" }}>{card.label}</Typography>
                <Typography sx={{ fontSize: 28, fontWeight: 300, color: "text.primary", fontFamily: "monospace", mt: 0.5 }}>{card.value}</Typography>
              </Paper>
            ))}
          </Box>

          {/* Scheduler Settings Card */}
          <Paper elevation={0} sx={{ p: 2, borderRadius: "16px", background: glassBg, border: `1px solid ${glassBorder}` }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5, flexWrap: "wrap", gap: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary" }}>Live Ingestion Scheduler</Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, px: 1, py: 0.25, borderRadius: "999px", background: csmIndiaScheduler.scheduler_enabled ? "rgba(16,185,129,0.12)" : "rgba(148,163,184,0.12)", border: `1px solid ${csmIndiaScheduler.scheduler_enabled ? "rgba(16,185,129,0.3)" : "rgba(148,163,184,0.3)"}` }}>
                  <Box sx={{ width: 7, height: 7, borderRadius: "50%", background: csmIndiaScheduler.scheduler_enabled ? "#10b981" : "#94a3b8", animation: csmIndiaScheduler.scheduler_enabled ? "pulse 2s infinite" : "none", "@keyframes pulse": { "0%,100%": { opacity: 1 }, "50%": { opacity: 0.3 } } }} />
                  <Typography sx={{ fontSize: 11, fontWeight: 700, color: csmIndiaScheduler.scheduler_enabled ? "#10b981" : "#94a3b8" }}>
                    {csmIndiaScheduler.scheduler_enabled ? "Running" : "Stopped"}
                  </Typography>
                </Box>
              </Box>
              {csmIndiaScheduler.updated_at && (
                <Typography sx={{ fontSize: 11, color: "text.disabled" }}>Last saved: {formatDate(csmIndiaScheduler.updated_at)}</Typography>
              )}
            </Box>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "flex-end" }}>
              <Box>
                <Typography sx={{ fontSize: 11, fontWeight: 600, color: "text.secondary", mb: 0.5 }}>Fetch Interval (minutes)</Typography>
                <TextField size="small" type="number" value={csmIndiaSchedulerDraft?.interval_minutes ?? 30}
                  onChange={e => setCsmIndiaSchedulerDraft(p => ({ ...p, interval_minutes: e.target.value }))}
                  inputProps={{ min: 1, max: 1440 }}
                  sx={{ width: 140, "& .MuiInputBase-root": { borderRadius: "10px", fontSize: 13 } }} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: 11, fontWeight: 600, color: "text.secondary", mb: 0.5 }}>Fetch Since (from date)</Typography>
                <TextField size="small" type="date" value={csmIndiaSchedulerDraft?.fetch_since || ""}
                  onChange={e => setCsmIndiaSchedulerDraft(p => ({ ...p, fetch_since: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  sx={{ width: 165, "& .MuiInputBase-root": { borderRadius: "10px", fontSize: 13 } }} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: 11, fontWeight: 600, color: "text.secondary", mb: 0.5 }}>Fetch Until (end date)</Typography>
                <TextField size="small" type="date" value={csmIndiaSchedulerDraft?.fetch_until || ""}
                  onChange={e => setCsmIndiaSchedulerDraft(p => ({ ...p, fetch_until: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  sx={{ width: 165, "& .MuiInputBase-root": { borderRadius: "10px", fontSize: 13 } }} />
              </Box>
              {(csmIndiaSchedulerDraft?.fetch_since || csmIndiaSchedulerDraft?.fetch_until) && (
                <Button size="small" variant="text"
                  onClick={() => setCsmIndiaSchedulerDraft(p => ({ ...p, fetch_since: "", fetch_until: "" }))}
                  sx={{ color: "text.disabled", fontSize: 11, textTransform: "none", alignSelf: "flex-end", mb: 0.25 }}>
                  Clear settings
                </Button>
              )}
              <Button size="small" variant="outlined"
                onClick={() => setCsmIndiaSchedulerDraft(p => ({ ...p, scheduler_enabled: !p.scheduler_enabled }))}
                sx={{ borderRadius: "10px", textTransform: "none", fontSize: 12, alignSelf: "flex-end", borderColor: csmIndiaSchedulerDraft?.scheduler_enabled ? "rgba(220,38,38,0.4)" : "rgba(16,185,129,0.4)", color: csmIndiaSchedulerDraft?.scheduler_enabled ? "#dc2626" : "#10b981", "&:hover": { background: csmIndiaSchedulerDraft?.scheduler_enabled ? "rgba(220,38,38,0.06)" : "rgba(16,185,129,0.06)" } }}>
                {csmIndiaSchedulerDraft?.scheduler_enabled ? "Stop Scheduler" : "Start Scheduler"}
              </Button>
              <Button size="small" variant="contained" onClick={handleSaveIndiaScheduler} disabled={csmIndiaSchedulerSaving}
                startIcon={csmIndiaSchedulerSaving ? <CircularProgress size={13} sx={{ color: "#fff" }} /> : <SaveIcon sx={{ fontSize: 14 }} />}
                sx={{ borderRadius: "10px", textTransform: "none", fontSize: 12, background: "#F26522", "&:hover": { background: "#d4551a" }, alignSelf: "flex-end" }}>
                Save Settings
              </Button>
            </Box>
            <Typography sx={{ fontSize: 11, color: "text.disabled", mt: 1 }}>
              Changes take effect on the next scheduler cycle. Polls both India mailboxes (Sales &amp; Marketing, Delivery/BA). "Fetch Since/Until" filters which emails are ingested by date.
            </Typography>
          </Paper>

          {/* Portfolio Graph + Actions */}
          <Paper elevation={0} sx={{ p: 2, borderRadius: "16px", background: glassBg, border: `1px solid ${glassBorder}`, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary" }}>Portfolio Graph (India)</Typography>
              <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                Last updated: {csmIndiaStatus?.last_updated ? formatDate(csmIndiaStatus.last_updated) : "Never"}
                {" · "}Processed items: {csmIndiaStatus?.processed_items_total ?? 0}
                {csmIndiaStatus?.processed_by_mailbox && Object.keys(csmIndiaStatus.processed_by_mailbox).length > 0 && (
                  <span> ({Object.entries(csmIndiaStatus.processed_by_mailbox).map(([k, v]) => `${v} ${MAILBOX_LABELS[k] || k}`).join(", ")})</span>
                )}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Button variant="outlined" size="small" onClick={fetchCsmIndiaData} startIcon={<RefreshIcon sx={{ fontSize: 15 }} />}
                sx={{ borderRadius: "999px", textTransform: "none", fontSize: 12, borderColor: "rgba(242,101,34,0.3)", color: "#F26522", "&:hover": { borderColor: "#F26522", background: "rgba(242,101,34,0.06)" } }}>
                Refresh
              </Button>
              <Button variant="outlined" size="small" disabled={csmIndiaLiveIngesting}
                onClick={async () => {
                  setCsmIndiaLiveIngesting(true); setCsmIndiaMessage(""); setCsmIndiaError("");
                  try {
                    const token = localStorage.getItem("edge_token");
                    const res = await axios.post("/api/admin/csm/india/ingest/live", {}, { headers: { Authorization: `Bearer ${token}` } });
                    setCsmIndiaMessage(`Live ingestion complete. Added: ${res.data.total_added_signals}, Updated: ${res.data.total_updated_signals}, Items: ${res.data.processed_items?.length ?? 0}`);
                    fetchCsmIndiaData();
                  } catch (e) { setCsmIndiaError("Live ingestion failed: " + (e.response?.data?.detail || e.message)); }
                  finally { setCsmIndiaLiveIngesting(false); }
                }}
                sx={{ borderRadius: "999px", textTransform: "none", fontSize: 12, borderColor: "rgba(16,185,129,0.4)", color: "#10b981", "&:hover": { borderColor: "#10b981", background: "rgba(16,185,129,0.06)" } }}>
                {csmIndiaLiveIngesting ? <CircularProgress size={14} sx={{ color: "#10b981" }} /> : "Run Live Ingestion"}
              </Button>
              <Button variant="outlined" size="small"
                onClick={async () => {
                  if (!window.confirm("This will allow all India emails to be re-ingested. Continue?")) return;
                  try {
                    const token = localStorage.getItem("edge_token");
                    await axios.delete("/api/admin/csm/india/processed-items", { headers: { Authorization: `Bearer ${token}` } });
                    setCsmIndiaMessage("Processed item history cleared. Next ingestion will re-process all items.");
                    fetchCsmIndiaData();
                  } catch (e) { setCsmIndiaError("Failed to clear history."); }
                }}
                sx={{ borderRadius: "999px", textTransform: "none", fontSize: 12, borderColor: "rgba(220,38,38,0.3)", color: "#dc2626", "&:hover": { borderColor: "#dc2626", background: "rgba(220,38,38,0.06)" } }}>
                Clear History
              </Button>
            </Box>
          </Paper>
        </Box>
      )}

      {/* TAB 1 — Processed Items table, full height */}
      {csmIndiaInnerTab === 1 && (
        <Paper elevation={0} sx={{ background: glassBg, backdropFilter: "blur(12px)", borderRadius: "16px", border: `1px solid ${glassBorder}`, display: "flex", flexDirection: "column", flex: 1, minHeight: 0, overflow: "hidden" }}>
          <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid var(--ae-border)", display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", flexShrink: 0 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ color: "text.primary", fontWeight: 700, fontSize: 15 }}>Processed Items</Typography>
              <Typography sx={{ color: "text.secondary", fontSize: 12 }}>Emails ingested from both CSM India mailboxes into the India portfolio graph.</Typography>
            </Box>
            <TextField size="small" placeholder="Filter by mailbox…" value={csmIndiaMailboxFilter} onChange={e => { setCsmIndiaMailboxFilter(e.target.value); setCsmIndiaPage(0); }}
              sx={{ width: 180, "& .MuiOutlinedInput-root": { borderRadius: "999px", fontSize: 12 } }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 15, color: "text.secondary" }} /></InputAdornment> }} />
            <TextField size="small" placeholder="Filter by name / sender / ID…" value={csmIndiaIdFilter} onChange={e => { setCsmIndiaIdFilter(e.target.value); setCsmIndiaPage(0); }}
              sx={{ width: 220, "& .MuiOutlinedInput-root": { borderRadius: "999px", fontSize: 12 } }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 15, color: "text.secondary" }} /></InputAdornment> }} />
          </Box>
          <TableContainer sx={{ flex: 1, overflowY: "auto" }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ bgcolor: headerBg, fontWeight: 700, fontSize: 12, width: 110 }}>Type</TableCell>
                  <TableCell sx={{ bgcolor: headerBg, fontWeight: 700, fontSize: 12, width: 150 }}>Mailbox</TableCell>
                  <TableCell sx={{ bgcolor: headerBg, fontWeight: 700, fontSize: 12 }}>Subject</TableCell>
                  <TableCell sx={{ bgcolor: headerBg, fontWeight: 700, fontSize: 12, width: 180 }}>Sender</TableCell>
                  <TableCell sx={{ bgcolor: headerBg, fontWeight: 700, fontSize: 12, width: 170 }}>Received At</TableCell>
                  <TableCell sx={{ bgcolor: headerBg, fontWeight: 700, fontSize: 12, width: 170 }}>Processed At</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {csmIndiaLoading ? (
                  <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4 }}><CircularProgress size={20} sx={{ color: "#F26522" }} /></TableCell></TableRow>
                ) : filteredCsmIndiaItems.length === 0 ? (
                  <TableRow><TableCell colSpan={6} align="center" sx={{ color: "text.secondary", py: 4, fontSize: 13 }}>No processed items yet. Run an ingestion to populate this table.</TableCell></TableRow>
                ) : (
                  filteredCsmIndiaItems.slice(csmIndiaPage * csmIndiaRowsPerPage, csmIndiaPage * csmIndiaRowsPerPage + csmIndiaRowsPerPage).map(item => (
                    <TableRow key={item.id} sx={{ "&:hover": { bgcolor: "var(--ae-surface)" } }}>
                      <TableCell>
                        <Chip label={TYPE_LABELS[item.item_type] || item.item_type} size="small"
                          sx={{ bgcolor: `${TYPE_COLORS[item.item_type] || "#6366f1"}18`, color: TYPE_COLORS[item.item_type] || "#6366f1", fontWeight: 700, fontSize: 11, height: 22 }} />
                      </TableCell>
                      <TableCell>
                        <Chip label={MAILBOX_LABELS[item.mailbox_team] || item.mailbox_team} size="small"
                          sx={{ bgcolor: `${MAILBOX_COLORS[item.mailbox_team] || "#6366f1"}18`, color: MAILBOX_COLORS[item.mailbox_team] || "#6366f1", fontWeight: 700, fontSize: 11, height: 22 }} />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 500 }}>
                        <Tooltip title={item.item_id} placement="top" arrow>
                          <Box>
                            <span style={{ fontSize: 12, color: "var(--ae-text-primary)", display: "block" }}>{item.item_name || "(no name)"}</span>
                            <span style={{ fontSize: 10, color: "var(--ae-text-secondary)", fontFamily: "monospace" }}>
                              {item.item_id.length > 40 ? item.item_id.slice(0, 40) + "…" : item.item_id}
                            </span>
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ color: "text.secondary", fontSize: 12 }}>{item.sender || "—"}</TableCell>
                      <TableCell sx={{ color: "text.secondary", fontSize: 12 }}>{item.received_at ? formatDate(item.received_at) : "—"}</TableCell>
                      <TableCell sx={{ color: "text.secondary", fontSize: 12 }}>{formatDate(item.processed_at)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination component="div" count={filteredCsmIndiaItems.length} page={csmIndiaPage} onPageChange={(_, p) => setCsmIndiaPage(p)}
            rowsPerPage={csmIndiaRowsPerPage} onRowsPerPageChange={e => setCsmIndiaRowsPerPage(parseInt(e.target.value, 10))}
            rowsPerPageOptions={[10, 20, 50]} sx={{ borderTop: "1px solid var(--ae-border)", fontSize: 12, flexShrink: 0 }} />
        </Paper>
      )}
    </Box>
  );

  if (user && !hasAccess) {
    return (
      <Box sx={{ p: 4, textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100vh" }}>
        <Alert severity="warning" sx={{ maxWidth: 500, mb: 3 }}>
          Access restricted. You need Administrator or User Admin permissions to view this console.
        </Alert>
        <Button variant="contained" onClick={() => navigate("/")} sx={{ bgcolor: "#F26522", "&:hover": { bgcolor: "#d85417" } }}>
          Return to Dashboard
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ height: "100vh", width: "100vw", display: "flex", flexDirection: "column", overflow: "hidden", background: isDark ? "radial-gradient(ellipse 80% 60% at 15% 10%, rgba(242,101,34,0.06) 0%, transparent 60%), #0a1628" : "radial-gradient(ellipse 80% 60% at 15% 10%, rgba(242,101,34,0.04) 0%, transparent 60%), #f8fafc", p: { xs: 1.5, md: 2 } }}>
      {user && user.remaining_total_tokens !== undefined && user.remaining_total_tokens < 20000 && !warningDismissed && (
        <Alert severity="warning" onClose={() => setWarningDismissed(true)} sx={{ mb: 1.5, borderRadius: "16px", border: "1px solid rgba(242,101,34,0.2)", backgroundColor: "rgba(242,101,34,0.1)", color: isDark ? "#ffb74d" : "#b78103", fontWeight: "bold", "& .MuiAlert-icon": { color: "#F26522" } }}>
          Warning: Your remaining token balance is critically low ({user.remaining_total_tokens.toLocaleString()} tokens left). Please contact an administrator to increase your limit.
        </Alert>
      )}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1.5, flexShrink: 0 }}>
        <Button variant="outlined" onClick={() => navigate("/")} size="small" sx={{ px: 2, borderRadius: "999px", borderColor: "rgba(15,23,42,0.12)", color: "text.secondary", fontSize: "12px", textTransform: "none", "&:hover": { borderColor: "#F26522", color: "#F26522" } }}>
          {"← Dashboard"}
        </Button>
        <Typography variant="h5" sx={{ color: "#F26522", fontWeight: 800, fontFamily: "'Syne', sans-serif", flex: 1, letterSpacing: "-0.5px" }}>
          Admin Console
        </Typography>
        <Button variant="text" startIcon={<LogoutIcon sx={{ fontSize: 16 }} />} onClick={logout} size="small" sx={{ color: "text.secondary", borderRadius: "999px", fontSize: "12px", fontWeight: 700, textTransform: "none", "&:hover": { color: "#F26522" } }}>
          Logout
        </Button>
      </Box>

      {tab === 0 && (
        <Paper sx={{ p: 1.5, mb: 1.5, flexShrink: 0, borderRadius: "20px", background: isDark ? "rgba(15,23,42,0.4)" : "rgba(255,255,255,0.8)", backdropFilter: "blur(8px)", border: `1px solid ${glassBorder}`, boxShadow: isDark ? "0 8px 32px rgba(0,0,0,0.2)" : "0 4px 20px rgba(15,23,42,0.04)" }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
            <Box>
              <Typography sx={{ color: "text.primary", fontSize: 16, fontWeight: 800 }}>
                {isUserAdmin && !isSuperAdmin ? "User Management Console" : "Access Control"}
              </Typography>
              <Typography sx={{ color: "text.secondary", fontSize: 11 }}>
                {isUserAdmin && !isSuperAdmin
                  ? "Manage user approvals, roles, and limits for users created by you."
                  : "Manage user approvals and roles."}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
              <TextField
                size="small" placeholder="Search users..." value={userSearch}
                onChange={(event) => { setUserSearch(event.target.value); setUserPage(0); }}
                sx={{ width: { xs: 180, sm: 260 }, "& .MuiOutlinedInput-root": { borderRadius: "999px", background: isDark ? "rgba(255,255,255,0.04)" : "#fff", fontSize: 13 } }}
                InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon sx={{ color: "text.secondary", fontSize: 18 }} /></InputAdornment>) }}
              />
              <Button
                onClick={handleOpenAddUser}
                variant="contained"
                size="small"
                startIcon={<PersonAddIcon sx={{ fontSize: 16 }} />}
                sx={{
                  borderRadius: "999px",
                  background: "linear-gradient(135deg, #F26522 0%, #ff8c42 100%)",
                  color: "#fff",
                  fontWeight: 700,
                  textTransform: "none",
                  fontSize: 12,
                  px: 2,
                  boxShadow: "0 4px 14px rgba(242,101,34,0.35)",
                  "&:hover": { background: "linear-gradient(135deg, #d85417 0%, #f26522 100%)" },
                }}
              >
                Add User
              </Button>
              <Button onClick={fetchUsers} variant="outlined" size="small" startIcon={<RefreshIcon sx={{ fontSize: 16 }} />} sx={{ borderRadius: "999px", borderColor: "rgba(242,101,34,0.3)", color: "#F26522", textTransform: "none", fontSize: 12, "&:hover": { borderColor: "#F26522", background: "rgba(242,101,34,0.06)" } }}>
                Refresh
              </Button>
              {(userSearch || userColFilters.email || userColFilters.roles || userColFilters.status) && (
                <Button onClick={() => { setUserSearch(""); setUserColFilters({ email: "", roles: "", status: "", creator: "" }); setUserPage(0); try { localStorage.removeItem("admin_userSearch"); localStorage.removeItem("admin_userColFilters"); } catch { } }} variant="outlined" size="small" sx={{ borderRadius: "999px", borderColor: "rgba(220,38,38,0.3)", color: "#dc2626", textTransform: "none", fontSize: 12, "&:hover": { borderColor: "#dc2626", background: "rgba(220,38,38,0.06)" } }}>
                  Clear Filters
                </Button>
              )}
            </Box>
          </Box>
        </Paper>
      )}

      {isSuperAdmin && (
        <Box sx={{ mb: 1, flexShrink: 0 }}>
          <Tabs value={tab} onChange={(_, nextTab) => setTab(nextTab)} textColor="inherit" variant="scrollable" scrollButtons="auto" TabIndicatorProps={{ style: { background: "#F26522", height: 3, borderRadius: "3px" } }} sx={{ borderBottom: "1px solid var(--ae-border)", "& .MuiTab-root": { color: "text.secondary", fontWeight: 800, textTransform: "none", fontSize: 13, minHeight: 44 }, "& .Mui-selected": { color: "#F26522 !important" } }}>
            <Tab label="Users & Access" />
            <Tab label="BRD Data Management" />
            <Tab label="BRD to SDD Management" />
            <Tab label="WF to SDD Management" />
            <Tab label="Test-Case Management" />
            <Tab label="Training Eval Management" />
            <Tab label="CSM Intelligence" />
            <Tab label="CSM India Intelligence" />
          </Tabs>
        </Box>
      )}

      {userError && <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }}>{userError}</Alert>}
      {brdError && <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }}>{brdError}</Alert>}
      {brdMessage && <Alert severity="success" sx={{ mb: 2, flexShrink: 0 }}>{brdMessage}</Alert>}

      {tab === 0 ? (
        userLoading ? <Box display="flex" justifyContent="center" sx={{ mt: 4 }}><CircularProgress sx={{ color: "#F26522" }} /></Box> : renderUserTable()
      ) : tab === 1 ? (
        brdLoading ? <Box display="flex" justifyContent="center" sx={{ mt: 4 }}><CircularProgress sx={{ color: "#F26522" }} /></Box> : renderBrdTable()
      ) : tab === 2 ? (
        sddBrdLoading ? <Box display="flex" justifyContent="center" sx={{ mt: 4 }}><CircularProgress sx={{ color: "#F26522" }} /></Box> : renderSddBrdTable()
      ) : tab === 3 ? (
        sddWfLoading ? <Box display="flex" justifyContent="center" sx={{ mt: 4 }}><CircularProgress sx={{ color: "#F26522" }} /></Box> : renderSddWfTable()
      ) : tab === 4 ? (
        testCaseLoading ? <Box display="flex" justifyContent="center" sx={{ mt: 4 }}><CircularProgress sx={{ color: "#F26522" }} /></Box> : renderTestCaseTable()
      ) : tab === 5 ? (
        trainingLoading ? <Box display="flex" justifyContent="center" sx={{ mt: 4 }}><CircularProgress sx={{ color: "#F26522" }} /></Box> : renderTrainingTable()
      ) : tab === 6 ? (
        csmLoading && !csmLoadedOnce ? (
          <Box display="flex" justifyContent="center" sx={{ mt: 4 }}><CircularProgress sx={{ color: "#F26522" }} /></Box>
        ) : renderCsmTab()
      ) : (
        csmIndiaLoading && !csmIndiaLoadedOnce ? (
          <Box display="flex" justifyContent="center" sx={{ mt: 4 }}><CircularProgress sx={{ color: "#F26522" }} /></Box>
        ) : renderCsmIndiaTab()
      )}
      <AddUserDialog
        open={addUserOpen}
        onClose={() => setAddUserOpen(false)}
        onCreated={(created) => setUsers((prev) => [created, ...prev])}
        isDark={isDark}
        isSuperAdmin={isSuperAdmin}
        isUserAdmin={isUserAdmin}
      />
    </Box>
  );
}